import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  limit,
  deleteDoc,
  CollectionReference,
  DocumentData,
  Firestore
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Device, DeviceSize, DeviceType, MaintenanceRecord, MaintenanceStatus, PaymentStatus, InventoryItem, User, UserRole, MaintenanceDevice } from '../utils/types';
import { updateCustomerLastVisit, getCustomerById } from './customerService';
import { recordTechnicianEarning } from './technicianService';
import { hybridWhatsAppService } from './hybridWhatsAppService';
import { getSystemSettings } from './systemSettingsService';
import { CounterType, incrementCounter } from './counterService';
// دالة إرسال رسالة استلام الجهاز تلقائياً
const sendDeviceReceivedMessage = async (maintenanceRecord: MaintenanceRecord): Promise<void> => {
  try {
    console.log('🔄 بدء عملية إرسال رسالة واتساب تلقائية...');
    console.log('📋 بيانات طلب الصيانة:', {
      id: maintenanceRecord.id,
      customerId: maintenanceRecord.customerId,
      customerName: maintenanceRecord.customerName,
      orderNumber: maintenanceRecord.orderNumber
    });

    // التحقق من إعدادات النظام
    console.log('⚙️ جاري التحقق من إعدادات النظام...');
    const settings = await getSystemSettings();

    console.log('📊 إعدادات الواتساب:', {
      whatsappEnabled: settings.whatsappEnabled,
      whatsappAutoSendOnCreate: settings.whatsappAutoSendOnCreate
    });

    // التحقق من تفعيل الإرسال التلقائي
    if (!settings.whatsappEnabled) {
      console.log('❌ الواتساب غير مفعل في الإعدادات');
      return;
    }

    if (!settings.whatsappAutoSendOnCreate) {
      console.log('❌ الإرسال التلقائي غير مفعل في الإعدادات');
      return;
    }

    // الحصول على بيانات العميل
    console.log('👤 جاري الحصول على بيانات العميل...');
    const customer = await getCustomerById(maintenanceRecord.customerId);
    if (!customer) {
      console.warn('❌ العميل غير موجود');
      return;
    }

    if (!customer.phone) {
      console.warn('❌ رقم هاتف العميل غير متوفر');
      return;
    }

    console.log('📱 بيانات العميل:', {
      name: customer.name,
      phone: customer.phone
    });

    // إرسال رسالة استلام الجهاز
    console.log('📤 جاري إرسال رسالة الواتساب...');
    const result = await hybridWhatsAppService.sendDeviceReceivedMessage(maintenanceRecord);

    if (result) {
      console.log(`✅ تم إرسال رسالة الواتساب بنجاح للعميل: ${customer.name}`);
    } else {
      console.log(`❌ فشل في إرسال رسالة الواتساب للعميل: ${customer.name}`);
    }
  } catch (error) {
    console.error('❌ خطأ في إرسال رسالة الواتساب التلقائية:', error);
    // لا نرمي الخطأ لأن فشل إرسال الرسالة لا يجب أن يؤثر على إنشاء طلب الصيانة
  }
};

// دالة بديلة لإرسال رسائل واتساب
const sendMaintenanceCompletedMessage = async (record: any, customer: any, useSenderPhone: boolean) => {
  console.log('Sending maintenance completed message', record, customer, useSenderPhone);
  return { success: true };
};
import { updateInventoryQuantityInWarehouse, getInventoryQuantityInWarehouse, getWarehouseById } from './inventoryService';
import { getBranchById } from './branchService';
import { getUserById } from './userService';
import { logActivity } from './activityLogService';
import { ActivityType, ActivityLevel } from '../types/activityLog';

// MaintenancePart interface for parts used in maintenance
export interface MaintenancePart {
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
  warehouseId: string;
  warehouseName?: string;
}

// Helper function to ensure Firestore is available
const ensureFirestore = (): Firestore => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }
  return db;
};

// Helper function to clean data and remove undefined values
const cleanFirestoreData = (data: any): any => {
  const cleaned: any = {};

  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        // تنظيف المصفوفات
        cleaned[key] = value.map(item =>
          typeof item === 'object' && item !== null ? cleanFirestoreData(item) : item
        );
      } else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
        // تنظيف الكائنات
        cleaned[key] = cleanFirestoreData(value);
      } else {
        cleaned[key] = value;
      }
    }
  }

  return cleaned;
};

// Helper functions to get collection references
const getMaintenanceCollection = () => {
  return collection(ensureFirestore(), 'maintenance');
};

const getDevicesCollection = () => {
  return collection(ensureFirestore(), 'devices');
};

// Add a new device for a customer
export const addDevice = async (deviceData: Omit<Device, 'id'>): Promise<Device> => {
  try {
    // التحقق من البيانات المطلوبة
    if (!deviceData.customerId) {
      throw new Error('معرف العميل مطلوب لإضافة الجهاز');
    }

    if (!deviceData.type) {
      throw new Error('نوع الجهاز مطلوب');
    }

    // التحقق من صحة معرف العميل
    const customerExists = await getDoc(doc(ensureFirestore(), 'customers', deviceData.customerId));
    if (!customerExists.exists()) {
      throw new Error('العميل المحدد غير موجود في قاعدة البيانات');
    }

    console.log('إضافة جهاز جديد:', {
      customerId: deviceData.customerId,
      type: deviceData.type,
      brand: deviceData.brand,
      model: deviceData.model
    });

    const devicesCollection = getDevicesCollection();
    const newDeviceRef = await addDoc(devicesCollection, {
      ...deviceData,
      createdAt: new Date(),
      updatedAt: new Date()
    });

    const newDeviceDoc = await getDoc(newDeviceRef);
    if (!newDeviceDoc.exists()) {
      throw new Error('فشل في إنشاء الجهاز - لم يتم العثور على الجهاز المُنشأ');
    }

    const newDeviceData = newDeviceDoc.data();
    if (!newDeviceData) {
      throw new Error('فشل في قراءة بيانات الجهاز المُنشأ');
    }

    console.log('تم إنشاء الجهاز بنجاح:', newDeviceRef.id);

    return {
      id: newDeviceDoc.id,
      ...newDeviceData
    } as Device;
  } catch (error: any) {
    console.error('Error adding device:', error);

    // تحسين رسائل الخطأ
    if (error.code === 'permission-denied') {
      throw new Error('ليس لديك صلاحية لإضافة الأجهزة');
    } else if (error.code === 'unavailable') {
      throw new Error('الخدمة غير متاحة حالياً، يرجى المحاولة مرة أخرى');
    } else if (error.code === 'unauthenticated') {
      throw new Error('يجب تسجيل الدخول أولاً');
    } else if (error.message && error.message.includes('required')) {
      throw error; // إعادة توجيه رسائل التحقق المخصصة
    } else {
      throw new Error(`فشل في إضافة الجهاز: ${error.message || 'خطأ غير معروف'}`);
    }
  }
};

// Get a device by ID
export const getDeviceById = async (deviceId: string): Promise<Device | null> => {
  try {
    const firestore = ensureFirestore();
    const deviceDoc = await getDoc(doc(firestore, 'devices', deviceId));

    if (deviceDoc.exists()) {
      const deviceData = deviceDoc.data();
      return {
        id: deviceDoc.id,
        ...deviceData
      } as Device;
    }

    return null;
  } catch (error) {
    console.error('Error getting device:', error);
    return null;
  }
};

// Get all devices for a customer
export const getDevicesByCustomerId = async (customerId: string): Promise<Device[]> => {
  try {
    const devicesCollection = getDevicesCollection();
    const q = query(
      devicesCollection,
      where('customerId', '==', customerId)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data
      } as Device;
    });
  } catch (error) {
    console.error('Error getting devices by customer ID:', error);
    return [];
  }
};

// Function to map parts data to InventoryItem
const mapPartsToInventoryItems = (parts: any[]): InventoryItem[] => {
  return parts.map(part => ({
    id: part.itemId || part.id || '',
    name: part.name || '',
    sku: part.sku || '',
    quantity: part.quantity || 0,
    sellingPrice: part.price || part.sellingPrice || 0,
    purchasePrice: part.price || part.purchasePrice || 0,
    minQuantity: part.minQuantity || 0,
    warehouseId: part.warehouseId || '',
    warehouseName: part.warehouseName || '',
    category: part.category || '',
    barcode: part.barcode || '',
    description: part.description || '',
    imageUrl: part.imageUrl || '',
    showInStore: part.showInStore || false,
    createdAt: part.createdAt || new Date(),
    updatedAt: part.updatedAt || new Date()
  }));
};

// Error handler for getMaintenanceById function - handle missing properties
const mapFirestoreDataToMaintenanceRecord = (id: string, data: any): MaintenanceRecord => {
  // Convert usedParts array to proper InventoryItem objects with 'total' custom property
  const usedPartsWithTotal = data.usedParts ?
    data.usedParts.map((part: any) => {
      const inventoryItem = mapPartsToInventoryItems([part])[0];
      // Preserve the total property if it exists
      if (part.total !== undefined) {
        (inventoryItem as any).total = part.total;
      }
      return inventoryItem;
    }) : [];

  // Handle devices array - if it exists, use it; otherwise create from legacy fields
  let devices: MaintenanceDevice[] = [];
  if (data.devices && Array.isArray(data.devices)) {
    devices = data.devices.map((device: any) => ({
      id: device.id || `device-${Date.now()}`,
      deviceId: device.deviceId || data.deviceId || '',
      deviceType: device.deviceType || data.deviceType || 'أخرى',
      brand: device.brand || '',
      model: device.model || '',
      serialNumber: device.serialNumber || '',
      size: device.size,
      issue: device.issue || data.issue || '',
      diagnosis: device.diagnosis || data.diagnosis,
      accessories: device.accessories || data.accessories || [],
      usedParts: device.usedParts ? mapPartsToInventoryItems(device.usedParts) : [],
      status: device.status || data.status || 'pending',
      completedAt: device.completedAt ?
        (device.completedAt instanceof Timestamp ?
          device.completedAt.toDate() :
          new Date(device.completedAt)) :
        undefined,
      deliveredAt: device.deliveredAt ?
        (device.deliveredAt instanceof Timestamp ?
          device.deliveredAt.toDate() :
          new Date(device.deliveredAt)) :
        undefined,
      notes: device.notes || ''
    }));
  } else {
    // Create single device from legacy fields for backward compatibility
    devices = [{
      id: `device-${Date.now()}`,
      deviceId: data.deviceId || '',
      deviceType: data.deviceType || 'أخرى',
      brand: data.brand || '',
      model: data.model || '',
      serialNumber: data.serialNumber || '',
      size: data.size,
      issue: data.issue || '',
      diagnosis: data.diagnosis,
      accessories: data.accessories || [],
      usedParts: usedPartsWithTotal,
      status: data.status || 'pending',
      completedAt: data.completedAt ?
        (data.completedAt instanceof Timestamp ?
          data.completedAt.toDate() :
          new Date(data.completedAt)) :
        undefined,
      deliveredAt: data.deliveredAt ?
        (data.deliveredAt instanceof Timestamp ?
          data.deliveredAt.toDate() :
          new Date(data.deliveredAt)) :
        undefined,
      notes: data.notes || ''
    }];
  }

  return {
    id,
    orderNumber: data.orderNumber || 0,
    customerId: data.customerId || '',
    customerName: data.customerName || '',
    customerPhone: data.customerPhone || '',
    devices,
    receivedAt: data.receivedAt instanceof Timestamp ? data.receivedAt.toDate() : new Date(data.receivedAt || Date.now()),
    estimatedDeliveryDate: data.estimatedDeliveryDate ?
      (data.estimatedDeliveryDate instanceof Timestamp ?
        data.estimatedDeliveryDate.toDate() :
        new Date(data.estimatedDeliveryDate)) :
      undefined,
    actualDeliveryDate: data.actualDeliveryDate ?
      (data.actualDeliveryDate instanceof Timestamp ?
        data.actualDeliveryDate.toDate() :
        new Date(data.actualDeliveryDate)) :
      undefined,
    completedAt: data.completedAt ?
      (data.completedAt instanceof Timestamp ?
        data.completedAt.toDate() :
        new Date(data.completedAt)) :
      undefined,
    deliveredAt: data.deliveredAt ?
      (data.deliveredAt instanceof Timestamp ?
        data.deliveredAt.toDate() :
        new Date(data.deliveredAt)) :
      undefined,
    receivedByEmployeeId: data.receivedByEmployeeId,
    receivedByEmployeeName: data.receivedByEmployeeName,
    technicianId: data.technicianId,
    technicianName: data.technicianName,
    branchId: data.branchId || '',
    branchName: data.branchName || '',
    warehouseId: data.warehouseId || '',
    warehouseName: data.warehouseName || '',
    status: data.status || 'pending',

    // Legacy fields for backward compatibility (derived from first device)
    deviceId: devices[0]?.deviceId || '',
    deviceType: devices[0]?.deviceType || 'أخرى',
    issue: devices[0]?.issue || '',
    diagnosis: devices[0]?.diagnosis,
    accessories: devices[0]?.accessories || [],
    usedParts: usedPartsWithTotal, // استخدام قطع الغيار من مستوى طلب الصيانة وليس من الجهاز

    // Financial fields
    initialCost: data.initialCost || 0,
    finalCost: data.finalCost || data.initialCost || 0,
    maintenanceFee: data.maintenanceFee || 0,
    paid: data.paid || 0,
    technicianShare: data.technicianShare || 0,
    paymentStatus: data.paymentStatus || 'unpaid',
    notes: data.notes || '',
    requireCallBeforeMaintenance: data.requireCallBeforeMaintenance || false,
    costAfterContact: data.costAfterContact || 0,

    // New cost system fields
    estimatedCost: data.estimatedCost || data.initialCost || 0,
    partsCost: data.partsCost || 0,
    laborCost: data.laborCost || 0,
    totalPaid: data.totalPaid || data.paid || 0,
    remainingAmount: data.remainingAmount || Math.max(0, (data.finalCost || data.initialCost || 0) - (data.totalPaid || data.paid || 0)),
    hasDebt: data.hasDebt || false,
    paymentMethod: data.paymentMethod,
    paymentDate: data.paymentDate ? (data.paymentDate instanceof Timestamp ? data.paymentDate.toDate() : new Date(data.paymentDate)) : undefined,
    debtAmount: data.debtAmount || 0,
    debtDueDate: data.debtDueDate ? (data.debtDueDate instanceof Timestamp ? data.debtDueDate.toDate() : new Date(data.debtDueDate)) : undefined,
    debtId: data.debtId,
    customerNotified: data.customerNotified || false,
    customerApproved: data.customerApproved || false,

    // Images field
    images: data.images || []
  };
};

// Create an enhanced maintenance record with device details
export const createEnhancedMaintenanceRecord = async (
  deviceId: string,
  customerId: string,
  customerName: string,
  deviceType: DeviceType,
  brand: string,
  model: string,
  serialNumber: string,
  size: string | undefined,
  issue: string,
  initialCost: number,
  branchId: string,
  warehouseId: string,
  estimatedDeliveryDate?: Date,
  employeeId?: string,
  employeeName?: string,
  accessories?: string[],
  usedParts?: MaintenancePart[],
  finalCost?: number,
  paid?: number,
  paymentStatus?: PaymentStatus,
  notes?: string,
  requireCallBeforeMaintenance?: boolean,
  costAfterContact?: number
): Promise<MaintenanceRecord> => {
  try {
    // التحقق من الفرع
    const branch = await getBranchById(branchId);
    if (!branch) {
      throw new Error(`الفرع غير موجود: ${branchId}`);
    }

    // التحقق من المخزن
    if (!branch.warehouseIds.includes(warehouseId)) {
      throw new Error(`المخزن غير مرتبط بالفرع المحدد`);
    }

    // Calculate the maintenance fee (initialCost - parts cost)
    let partsCost = 0;
    let techShare = 0;

    if (usedParts && usedParts.length > 0) {
      partsCost = usedParts.reduce((sum, part) => sum + part.total, 0);
    }

    const maintenanceFee = initialCost - partsCost;

    if (paid && paid > 0) {
      // Calculate technician share
      techShare = paid - partsCost > 0 ? paid - partsCost : 0;
    }

    // Transform usedParts from MaintenancePart to InventoryItem
    const transformedUsedParts = usedParts ? usedParts.map(part => {
      const inventoryItem: InventoryItem = {
        id: part.itemId,
        name: part.name,
        quantity: part.quantity,
        sellingPrice: part.price,
        purchasePrice: part.price,
        sku: '',
        category: '',
        minQuantity: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        warehouseId: part.warehouseId,
        warehouseName: part.warehouseName || ''
      };
      // Add total as a custom property
      (inventoryItem as any).total = part.total;
      return inventoryItem;
    }) : [];

    // Obtener un número secuencial para la orden de mantenimiento
    const orderNumber = await incrementCounter(CounterType.MAINTENANCE);

    const maintenanceData: any = {
      orderNumber,
      deviceId,
      customerId,
      customerName,
      deviceType,
      brand,
      model,
      serialNumber,
      issue,
      initialCost,
      finalCost: finalCost || initialCost,
      maintenanceFee,
      technicianShare: techShare,
      branchId,
      branchName: branch.name,
      warehouseId,
      warehouseName: branch.warehouseIds.find(id => id === warehouseId) || 'مخزن غير معروف',
      status: 'pending' as MaintenanceStatus,
      receivedAt: new Date(),
      estimatedDeliveryDate: estimatedDeliveryDate || null,
      paid: paid || 0,
      paymentStatus: paymentStatus || 'unpaid',
      accessories: accessories || [],
      usedParts: transformedUsedParts,
      notes: notes || '',
      requireCallBeforeMaintenance: requireCallBeforeMaintenance || false,
      costAfterContact: costAfterContact || 0
    };

    // إضافة الحجم فقط إذا كان موجوداً
    if (size) {
      maintenanceData.size = size;
    }

    if (employeeId) {
      maintenanceData.receivedByEmployeeId = employeeId;
    }

    if (employeeName) {
      maintenanceData.receivedByEmployeeName = employeeName;
    }

    const maintenanceCollection = getMaintenanceCollection();
    const maintenanceRef = await addDoc(maintenanceCollection, maintenanceData);

    // Update the customer's last visit date
    await updateCustomerLastVisit(customerId);

    // حفظ بيانات المخزون المستخدمة للمحاولة لاحقًا
    let inventoryUpdateSuccess = true;
    let inventoryErrors = [];

    // خصم قطع الغيار المستخدمة من المخزون
    if (usedParts && usedParts.length > 0) {
      for (const part of usedParts) {
        try {
          // استخدام المخزن المحدد في الجزء، أو استخدام المخزن المرتبط بالصيانة إذا لم يكن محددًا
          const partWarehouseId = part.warehouseId || warehouseId;

          // التحقق من وجود المخزن قبل محاولة تحديث الكمية
          const warehouseExists = await getWarehouseById(partWarehouseId);
          if (!warehouseExists) {
            console.error(`المخزن غير موجود: ${partWarehouseId}`);
            inventoryUpdateSuccess = false;
            inventoryErrors.push(`المخزن غير موجود: ${partWarehouseId}`);
            continue;
          }

          // التحقق من وجود المنتج في المخزن ومن توفر الكمية المطلوبة
          const availableQuantity = await getInventoryQuantityInWarehouse(part.itemId, partWarehouseId);
          if (availableQuantity < part.quantity) {
            console.error(`الكمية المتوفرة (${availableQuantity}) أقل من الكمية المطلوبة (${part.quantity}) للمنتج ${part.name}`);
            inventoryUpdateSuccess = false;
            inventoryErrors.push(`الكمية غير كافية للمنتج ${part.name}`);
            continue;
          }

          // تحديث الكمية في المخزن
          await updateInventoryQuantityInWarehouse(
            part.itemId,
            partWarehouseId,
            -part.quantity,
            employeeId,
            `استخدام في طلب صيانة رقم ${maintenanceRef.id}`
          );
        } catch (partError) {
          console.error(`خطأ في تحديث كمية المنتج ${part.name}:`, partError);
          inventoryUpdateSuccess = false;
          inventoryErrors.push(`خطأ في تحديث كمية المنتج ${part.name}: ${partError instanceof Error ? partError.message : 'خطأ غير معروف'}`);
        }
      }
    }

    // إذا كانت هناك مشكلة في تحديث المخزون، قم بتسجيل ذلك في ملاحظات الصيانة
    if (!inventoryUpdateSuccess) {
      const updatedNotes = notes ? `${notes}\n\nتحذير: حدثت مشكلة في تحديث المخزون: ${inventoryErrors.join(', ')}` :
        `تحذير: حدثت مشكلة في تحديث المخزون: ${inventoryErrors.join(', ')}`;

      await updateDoc(maintenanceRef, { notes: updatedNotes });
    }

    // Get the newly created document
    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      throw new Error(`Newly created maintenance record with ID ${maintenanceRef.id} not found`);
    }

    const newMaintenanceData = maintenanceDoc.data();
    if (!newMaintenanceData) {
      throw new Error(`Data for maintenance record with ID ${maintenanceRef.id} is undefined`);
    }

    const maintenanceRecord = mapFirestoreDataToMaintenanceRecord(maintenanceRef.id, newMaintenanceData);

    // إرسال رسالة واتساب تلقائياً إذا كان مفعلاً في الإعدادات
    await sendDeviceReceivedMessage(maintenanceRecord);

    return maintenanceRecord;
  } catch (error) {
    console.error('Error creating enhanced maintenance record:', error);
    throw error;
  }
};

// Create a new maintenance record
export const createMaintenanceRecord = async (
  deviceId: string,
  customerId: string,
  customerName: string,
  deviceType: DeviceType,
  issue: string,
  initialCost: number,
  branchId: string,
  warehouseId: string,
  estimatedDeliveryDate?: Date,
  employeeId?: string,
  employeeName?: string,
  accessories?: string[],
  usedParts?: MaintenancePart[],
  finalCost?: number,
  paid?: number,
  paymentStatus?: PaymentStatus,
  notes?: string,
  requireCallBeforeMaintenance?: boolean,
  costAfterContact?: number
): Promise<MaintenanceRecord> => {
  try {
    // التحقق من الفرع
    const branch = await getBranchById(branchId);
    if (!branch) {
      throw new Error(`الفرع غير موجود: ${branchId}`);
    }

    // التحقق من المخزن
    if (!branch.warehouseIds.includes(warehouseId)) {
      throw new Error(`المخزن غير مرتبط بالفرع المحدد`);
    }

    // Calculate the maintenance fee (initialCost - parts cost)
    let partsCost = 0;
    let techShare = 0;

    if (usedParts && usedParts.length > 0) {
      partsCost = usedParts.reduce((sum, part) => sum + part.total, 0);
    }

    const maintenanceFee = initialCost - partsCost;

    if (paid && paid > 0) {
      // Calculate technician share
      techShare = paid - partsCost > 0 ? paid - partsCost : 0;
    }

    // Transform usedParts from MaintenancePart to InventoryItem
    const transformedUsedParts = usedParts ? usedParts.map(part => {
      const inventoryItem: InventoryItem = {
        id: part.itemId,
        name: part.name,
        quantity: part.quantity,
        sellingPrice: part.price,
        purchasePrice: part.price,
        sku: '',
        category: '',
        minQuantity: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        warehouseId: part.warehouseId,
        warehouseName: part.warehouseName || ''
      };
      // Add total as a custom property
      (inventoryItem as any).total = part.total;
      return inventoryItem;
    }) : [];

    // Obtener un número secuencial para la orden de mantenimiento
    const orderNumber = await incrementCounter(CounterType.MAINTENANCE);

    const maintenanceData: any = {
      orderNumber,
      deviceId,
      customerId,
      customerName,
      deviceType,
      issue,
      initialCost,
      finalCost: finalCost || initialCost,
      maintenanceFee,
      technicianShare: techShare,
      branchId,
      branchName: branch.name,
      warehouseId,
      warehouseName: branch.warehouseIds.find(id => id === warehouseId) || 'مخزن غير معروف',
      status: 'pending' as MaintenanceStatus,
      receivedAt: new Date(),
      estimatedDeliveryDate: estimatedDeliveryDate || null,
      paid: paid || 0,
      paymentStatus: paymentStatus || 'unpaid',
      accessories: accessories || [],
      usedParts: transformedUsedParts,
      notes: notes || '',
      requireCallBeforeMaintenance: requireCallBeforeMaintenance || false,
      costAfterContact: costAfterContact || 0
    };

    if (employeeId) {
      maintenanceData.receivedByEmployeeId = employeeId;
    }

    if (employeeName) {
      maintenanceData.receivedByEmployeeName = employeeName;
    }

    const maintenanceCollection = getMaintenanceCollection();
    const maintenanceRef = await addDoc(maintenanceCollection, maintenanceData);

    // Update the customer's last visit date
    await updateCustomerLastVisit(customerId);

    // حفظ بيانات المخزون المستخدمة للمحاولة لاحقًا
    let inventoryUpdateSuccess = true;
    let inventoryErrors = [];

    // خصم قطع الغيار المستخدمة من المخزون
    if (usedParts && usedParts.length > 0) {
      for (const part of usedParts) {
        try {
          // استخدام المخزن المحدد في الجزء، أو استخدام المخزن المرتبط بالصيانة إذا لم يكن محددًا
          const partWarehouseId = part.warehouseId || warehouseId;

          // التحقق من وجود المخزن قبل محاولة تحديث الكمية
          const warehouseExists = await getWarehouseById(partWarehouseId);
          if (!warehouseExists) {
            console.error(`المخزن غير موجود: ${partWarehouseId}`);
            inventoryUpdateSuccess = false;
            inventoryErrors.push(`المخزن غير موجود: ${partWarehouseId}`);
            continue;
          }

          // التحقق من وجود المنتج في المخزن ومن توفر الكمية المطلوبة
          const availableQuantity = await getInventoryQuantityInWarehouse(part.itemId, partWarehouseId);
          if (availableQuantity < part.quantity) {
            console.error(`الكمية المتوفرة (${availableQuantity}) أقل من الكمية المطلوبة (${part.quantity}) للمنتج ${part.name}`);
            inventoryUpdateSuccess = false;
            inventoryErrors.push(`الكمية غير كافية للمنتج ${part.name}`);
            continue;
          }

          // تحديث الكمية في المخزن
          await updateInventoryQuantityInWarehouse(
            part.itemId,
            partWarehouseId,
            -part.quantity,
            employeeId,
            `استخدام في طلب صيانة رقم ${maintenanceRef.id}`
          );
        } catch (partError) {
          console.error(`خطأ في تحديث كمية المنتج ${part.name}:`, partError);
          inventoryUpdateSuccess = false;
          inventoryErrors.push(`خطأ في تحديث كمية المنتج ${part.name}: ${partError instanceof Error ? partError.message : 'خطأ غير معروف'}`);
        }
      }
    }

    // إذا كانت هناك مشكلة في تحديث المخزون، قم بتسجيل ذلك في ملاحظات الصيانة
    if (!inventoryUpdateSuccess) {
      const updatedNotes = notes ? `${notes}\n\nتحذير: حدثت مشكلة في تحديث المخزون: ${inventoryErrors.join(', ')}` :
        `تحذير: حدثت مشكلة في تحديث المخزون: ${inventoryErrors.join(', ')}`;

      await updateDoc(maintenanceRef, { notes: updatedNotes });
    }

    // Get the newly created document
    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      throw new Error(`Newly created maintenance record with ID ${maintenanceRef.id} not found`);
    }

    const newMaintenanceData = maintenanceDoc.data();
    if (!newMaintenanceData) {
      throw new Error(`Data for maintenance record with ID ${maintenanceRef.id} is undefined`);
    }

    const maintenanceRecord = mapFirestoreDataToMaintenanceRecord(maintenanceRef.id, newMaintenanceData);

    // إرسال رسالة واتساب تلقائياً إذا كان مفعلاً في الإعدادات
    await sendDeviceReceivedMessage(maintenanceRecord);

    return maintenanceRecord;
  } catch (error) {
    console.error('Error creating maintenance record:', error);
    throw error;
  }
};

// Create separate maintenance records for multiple devices
export const createSeparateMaintenanceRecords = async (
  customerId: string,
  customerName: string,
  devices: Omit<MaintenanceDevice, 'id' | 'status' | 'completedAt' | 'deliveredAt'>[],
  branchId: string,
  warehouseId: string,
  estimatedDeliveryDate?: Date,
  employeeId?: string,
  employeeName?: string,
  notes?: string
): Promise<MaintenanceRecord[]> => {
  try {
    console.log('Starting createSeparateMaintenanceRecords with:', {
      customerId,
      customerName,
      devicesCount: devices.length,
      branchId,
      warehouseId
    });

    // التحقق من البيانات المطلوبة
    if (!customerId || !customerName) {
      throw new Error('معرف العميل واسم العميل مطلوبان');
    }

    if (!devices || devices.length === 0) {
      throw new Error('يجب إضافة جهاز واحد على الأقل');
    }

    if (!branchId || !warehouseId) {
      throw new Error('معرف الفرع والمخزن مطلوبان');
    }

    console.log('Validating branch...');
    // التحقق من الفرع
    const branch = await getBranchById(branchId);
    if (!branch) {
      throw new Error(`الفرع غير موجود: ${branchId}`);
    }
    console.log('Branch validated:', branch.name);

    console.log('Validating warehouse...');
    // التحقق من المخزن وجلب اسمه
    const warehouse = await getWarehouseById(warehouseId);
    if (!warehouse) {
      throw new Error(`المخزن غير موجود: ${warehouseId}`);
    }
    console.log('Warehouse validated:', warehouse.name);

    // إنشاء طلبات منفصلة لكل جهاز
    const createdRecords: MaintenanceRecord[] = [];

    for (let i = 0; i < devices.length; i++) {
      const device = devices[i];
      console.log(`Creating maintenance record for device ${i + 1}:`, device.deviceType);

      // إنشاء طلب صيانة منفصل لكل جهاز باستخدام دالة محسنة
      const maintenanceRecord = await createEnhancedMaintenanceRecord(
        device.deviceId || '',
        customerId,
        customerName,
        device.deviceType,
        device.brand || '',
        device.model || '',
        device.serialNumber || '',
        device.size,
        device.issue,
        0, // initialCost - سيتم تحديثها لاحقاً
        branchId,
        warehouseId,
        estimatedDeliveryDate,
        employeeId,
        employeeName,
        device.accessories || [],
        [], // usedParts - سيتم إضافتها لاحقاً
        0, // finalCost
        0, // paid
        'unpaid', // paymentStatus
        device.notes || notes || '',
        false, // requireCallBeforeMaintenance
        0 // costAfterContact
      );

      createdRecords.push(maintenanceRecord);
      console.log(`Created maintenance record ${i + 1} with ID:`, maintenanceRecord.id);
    }

    console.log(`Successfully created ${createdRecords.length} separate maintenance records`);
    return createdRecords;

  } catch (error) {
    console.error('Error creating separate maintenance records:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      customerId,
      customerName,
      devicesCount: devices.length,
      branchId,
      warehouseId
    });
    throw error;
  }
};

// Create a new maintenance record with multiple devices (legacy function - kept for compatibility)
export const createMultiDeviceMaintenanceRecord = async (
  customerId: string,
  customerName: string,
  devices: Omit<MaintenanceDevice, 'id' | 'status' | 'completedAt' | 'deliveredAt'>[],
  branchId: string,
  warehouseId: string,
  estimatedDeliveryDate?: Date,
  employeeId?: string,
  employeeName?: string,
  notes?: string
): Promise<MaintenanceRecord> => {
  try {
    console.log('Starting createMultiDeviceMaintenanceRecord with:', {
      customerId,
      customerName,
      devicesCount: devices.length,
      branchId,
      warehouseId
    });

    // التحقق من البيانات المطلوبة
    if (!customerId || !customerName) {
      throw new Error('معرف العميل واسم العميل مطلوبان');
    }

    if (!devices || devices.length === 0) {
      throw new Error('يجب إضافة جهاز واحد على الأقل');
    }

    if (!branchId || !warehouseId) {
      throw new Error('معرف الفرع والمخزن مطلوبان');
    }

    console.log('Validating branch...');
    // التحقق من الفرع
    const branch = await getBranchById(branchId);
    if (!branch) {
      throw new Error(`الفرع غير موجود: ${branchId}`);
    }
    console.log('Branch validated:', branch.name);

    console.log('Validating warehouse...');
    // التحقق من المخزن وجلب اسمه
    const warehouse = await getWarehouseById(warehouseId);
    if (!warehouse) {
      throw new Error(`المخزن غير موجود: ${warehouseId}`);
    }
    console.log('Warehouse validated:', warehouse.name);

    console.log('Processing devices...');
    // إنشاء معرفات فريدة للأجهزة وتنظيف البيانات
    const devicesWithIds: MaintenanceDevice[] = devices.map((device, index) => {
      console.log(`Processing device ${index + 1}:`, device);

      // التحقق من البيانات المطلوبة لكل جهاز
      if (!device.deviceType || !device.issue) {
        throw new Error(`الجهاز رقم ${index + 1}: نوع الجهاز والمشكلة مطلوبان`);
      }

      // تنظيف بيانات الجهاز وإزالة القيم undefined
      const processedDevice: any = {
        id: `device-${Date.now()}-${index}`,
        deviceId: device.deviceId || '',
        deviceType: device.deviceType,
        brand: device.brand || '',
        model: device.model || '',
        serialNumber: device.serialNumber || '',
        issue: device.issue,
        status: 'pending' as MaintenanceStatus,
        accessories: device.accessories || [],
        usedParts: device.usedParts || [],
        notes: device.notes || ''
      };

      // إضافة الحقول الاختيارية فقط إذا كانت موجودة
      if (device.size) {
        processedDevice.size = device.size;
      }

      if (device.diagnosis) {
        processedDevice.diagnosis = device.diagnosis;
      }

      // تنظيف نهائي لبيانات الجهاز
      const cleanDevice = cleanFirestoreData(processedDevice);
      console.log(`Processed device ${index + 1}:`, cleanDevice);
      return cleanDevice;
    });

    console.log('All devices processed successfully');

    console.log('Getting order number...');
    // Obtener un número secuencial للطلب
    const orderNumber = await incrementCounter(CounterType.MAINTENANCE);
    console.log('Order number obtained:', orderNumber);

    // حساب التكلفة الإجمالية من جميع الأجهزة
    // في الوقت الحالي، سنبدأ بتكلفة 0 ويمكن تحديثها لاحقاً
    const totalInitialCost = 0;
    console.log('Initial cost calculated:', totalInitialCost);

    // تنظيف البيانات وإزالة القيم undefined
    const cleanMaintenanceData: any = {
      orderNumber,
      customerId,
      customerName,
      devices: devicesWithIds,
      branchId,
      branchName: branch.name,
      warehouseId,
      warehouseName: warehouse.name,
      status: 'pending' as MaintenanceStatus,
      receivedAt: new Date(),

      // Legacy fields for backward compatibility (from first device)
      deviceId: devicesWithIds[0]?.deviceId || '',
      deviceType: devicesWithIds[0]?.deviceType || 'أخرى',
      brand: devicesWithIds[0]?.brand || '',
      model: devicesWithIds[0]?.model || '',
      serialNumber: devicesWithIds[0]?.serialNumber || '',
      size: devicesWithIds[0]?.size,
      issue: devicesWithIds[0]?.issue || '',
      accessories: devicesWithIds[0]?.accessories || [],
      usedParts: devicesWithIds[0]?.usedParts || [],

      // Financial fields
      initialCost: totalInitialCost,
      finalCost: totalInitialCost,
      maintenanceFee: totalInitialCost,
      paid: 0,
      technicianShare: 0,
      paymentStatus: 'unpaid',
      notes: notes || '',
      requireCallBeforeMaintenance: false,
      costAfterContact: 0
    };

    // إضافة الحقول الاختيارية فقط إذا كانت موجودة
    if (estimatedDeliveryDate) {
      cleanMaintenanceData.estimatedDeliveryDate = estimatedDeliveryDate;
    }

    if (devicesWithIds[0]?.diagnosis) {
      cleanMaintenanceData.diagnosis = devicesWithIds[0].diagnosis;
    }

    // إضافة معلومات الموظف إذا كانت متوفرة
    if (employeeId) {
      cleanMaintenanceData.receivedByEmployeeId = employeeId;
    }

    if (employeeName) {
      cleanMaintenanceData.receivedByEmployeeName = employeeName;
    }

    console.log('Clean maintenance data before final cleaning:', cleanMaintenanceData);

    // تنظيف نهائي للبيانات لإزالة أي قيم undefined
    const finalCleanData = cleanFirestoreData(cleanMaintenanceData);
    console.log('Final clean data:', finalCleanData);

    console.log('Getting maintenance collection...');
    const maintenanceCollection = getMaintenanceCollection();

    console.log('Adding document to Firestore...');
    const maintenanceRef = await addDoc(maintenanceCollection, finalCleanData);

    console.log('Maintenance record created with ID:', maintenanceRef.id);

    console.log('Updating customer last visit...');
    // Update the customer's last visit date
    await updateCustomerLastVisit(customerId);
    console.log('Customer last visit updated successfully');

    console.log('Getting newly created document...');
    // Get the newly created document
    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      throw new Error(`Newly created maintenance record with ID ${maintenanceRef.id} not found`);
    }

    const newMaintenanceData = maintenanceDoc.data();
    if (!newMaintenanceData) {
      throw new Error(`Data for maintenance record with ID ${maintenanceRef.id} is undefined`);
    }

    console.log('Mapping Firestore data to MaintenanceRecord...');
    const result = mapFirestoreDataToMaintenanceRecord(maintenanceRef.id, newMaintenanceData);
    console.log('Multi-device maintenance record created successfully:', result.id);

    return result;
  } catch (error) {
    console.error('Error creating multi-device maintenance record:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      customerId,
      customerName,
      devicesCount: devices.length,
      branchId,
      warehouseId
    });
    throw error;
  }
};

// Get a maintenance record by ID
export const getMaintenanceById = async (maintenanceId: string): Promise<MaintenanceRecord | null> => {
  try {
    const firestore = ensureFirestore();
    const maintenanceDoc = await getDoc(doc(firestore, 'maintenance', maintenanceId));

    if (maintenanceDoc.exists()) {
      const data = maintenanceDoc.data();
      const record = mapFirestoreDataToMaintenanceRecord(maintenanceDoc.id, data);
      return record;
    }

    return null;
  } catch (error) {
    console.error('❌ خطأ في جلب طلب الصيانة:', error);
    return null;
  }
};

// Get all maintenance records
export const getAllMaintenanceRecords = async (
  statusFilter?: MaintenanceStatus,
  limitCount: number = 100
): Promise<MaintenanceRecord[]> => {
  try {
    let q;

    if (statusFilter) {
      q = query(
        getMaintenanceCollection(),
        where('status', '==', statusFilter),
        orderBy('receivedAt', 'desc'),
        limit(limitCount)
      );
    } else {
      q = query(
        getMaintenanceCollection(),
        orderBy('receivedAt', 'desc'),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return mapFirestoreDataToMaintenanceRecord(doc.id, data);
    });
  } catch (error) {
    console.error('Error getting maintenance records:', error);
    return [];
  }
};

// Get maintenance records for a customer
export const getMaintenanceByCustomerId = async (customerId: string): Promise<MaintenanceRecord[]> => {
  try {
    const q = query(
      getMaintenanceCollection(),
      where('customerId', '==', customerId),
      orderBy('receivedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return mapFirestoreDataToMaintenanceRecord(doc.id, data);
    });
  } catch (error) {
    console.error('Error getting maintenance records by customer ID:', error);
    return [];
  }
};

// Update maintenance record with partial data
export const updateMaintenanceRecord = async (
  maintenanceId: string,
  updates: Partial<MaintenanceRecord>
): Promise<void> => {
  try {
    console.log('🔄 بدء تحديث طلب الصيانة:', maintenanceId);
    console.log('📝 البيانات المراد تحديثها:', updates);

    // التحقق من صحة معرف الطلب
    if (!maintenanceId || maintenanceId.trim() === '') {
      throw new Error('معرف طلب الصيانة مطلوب');
    }

    // التحقق من وجود بيانات للتحديث
    if (!updates || Object.keys(updates).length === 0) {
      console.warn('⚠️ لا توجد بيانات للتحديث');
      return;
    }

    const firestore = ensureFirestore();
    console.log('🔥 Firestore instance:', firestore ? 'متصل' : 'غير متصل');

    const maintenanceRef = doc(firestore, 'maintenance', maintenanceId);
    console.log('📄 مرجع المستند:', maintenanceRef.path);

    // التحقق من وجود المستند أولاً
    const docSnapshot = await getDoc(maintenanceRef);
    if (!docSnapshot.exists()) {
      throw new Error(`طلب الصيانة ${maintenanceId} غير موجود`);
    }
    console.log('✅ تم العثور على طلب الصيانة');

    // إزالة الحقول التي لا يجب تحديثها مباشرة
    const { id, ...updateData } = updates;

    // تنظيف البيانات من القيم undefined بشكل عميق
    const cleanedData = cleanFirestoreData(updateData);

    console.log('🧹 البيانات قبل التنظيف:', updateData);
    console.log('✨ البيانات بعد التنظيف:', cleanedData);

    const finalUpdateData = {
      ...cleanedData,
      updatedAt: serverTimestamp()
    };

    console.log('💾 البيانات النهائية للحفظ:', finalUpdateData);
    console.log('🔢 عدد الحقول المراد تحديثها:', Object.keys(finalUpdateData).length);
    console.log('🧹 البيانات بعد التنظيف:', Object.keys(cleanedData));

    await updateDoc(maintenanceRef, finalUpdateData);

    console.log(`✅ تم تحديث طلب الصيانة ${maintenanceId} بنجاح`);
  } catch (error: any) {
    console.error('❌ خطأ في تحديث طلب الصيانة:', error);
    console.error('📋 تفاصيل الخطأ:', {
      code: error?.code,
      message: error?.message,
      maintenanceId,
      updatesKeys: Object.keys(updates || {})
    });

    // إعادة رمي الخطأ الأصلي مع تفاصيل أكثر
    if (error?.code) {
      throw error; // خطأ Firestore أصلي
    } else {
      throw new Error(`فشل في تحديث طلب الصيانة: ${error?.message || 'خطأ غير معروف'}`);
    }
  }
};

// Update maintenance status
export const updateMaintenanceStatus = async (
  maintenanceId: string,
  status: MaintenanceStatus,
  actualDeliveryDate?: Date
): Promise<void> => {
  try {
    const maintenanceRef = doc(ensureFirestore(), 'maintenance', maintenanceId);
    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      throw new Error(`Maintenance record with ID ${maintenanceId} not found`);
    }

    const maintenanceData = maintenanceDoc.data();
    if (!maintenanceData) {
      throw new Error(`Data for maintenance record with ID ${maintenanceId} is undefined`);
    }
    const updateData: any = { status };

    // Agregar timestamp según el estado
    if (status === 'completed') {
      updateData.completedAt = new Date();
    } else if (status === 'delivered') {
      // تعيين تاريخ التسليم الفعلي إلى تاريخ اليوم تلقائياً
      const deliveryDate = actualDeliveryDate || new Date();
      updateData.actualDeliveryDate = deliveryDate;
      updateData.deliveredAt = deliveryDate;
      
      // منطق إنشاء أو تحديث الدين
      const totalPaid = maintenanceData.totalPaid || maintenanceData.paid || 0;
      const finalCost = maintenanceData.finalCost || maintenanceData.estimatedCost || maintenanceData.initialCost || 0;
      if (totalPaid < finalCost) {
        // استدعاء خدمة إنشاء الدين
        const { createDebtFromMaintenance } = await import('./debtService');
        await createDebtFromMaintenance(
          maintenanceId,
          maintenanceData.orderNumber || 0,
          maintenanceData.customerId,
          finalCost - totalPaid,
          `دين متبقي على طلب صيانة رقم #${maintenanceData.orderNumber || maintenanceId}`,
          maintenanceData.updatedBy || '',
          maintenanceData.updatedByName || '',
          maintenanceData.branchId,
          maintenanceData.branchName,
          deliveryDate
        );
        console.log(`✅ تم إنشاء أو تحديث دين لطلب الصيانة ${maintenanceId}`);
      }
    }

    await updateDoc(maintenanceRef, updateData);

    // تسجيل أجر الفني بناءً على حالة الصيانة
    await handleTechnicianEarningsByStatus(maintenanceId, status, maintenanceData);

    // إرسال رسائل الواتساب التلقائية عند تغيير الحالة
    try {
      // الحصول على بيانات العميل
      const customerId = maintenanceData.customerId;

      if (customerId) {
        const customer = await getCustomerById(customerId);

        if (customer && customer.phone) {
          // التحقق من إعدادات الواتساب
          const { getSystemSettings } = await import('./systemSettingsService');
          const settings = await getSystemSettings();

          if (settings.whatsappEnabled) {
            // إرسال رسالة حسب الحالة الجديدة
            if (status === 'in_progress' && maintenanceData.requireCallBeforeMaintenance) {
              // إرسال رسالة التكلفة أثناء العمل للعملاء الذين يطلبون الاتصال
              const { sendNewWhatsAppMessage } = await import('./whatsappService');
              try {
                await sendNewWhatsAppMessage('deviceInProgress', {
                  customerName: customer.name,
                  customerPhone: customer.phone,
                  deviceType: maintenanceData.deviceType || 'غير محدد',
                  cost: maintenanceData.costAfterContact ?
                    `${maintenanceData.costAfterContact} ${settings.currency || 'SAR'}` :
                    `${maintenanceData.finalCost || maintenanceData.initialCost} ${settings.currency || 'SAR'}`,
                  orderId: maintenanceData.orderNumber ? `#${maintenanceData.orderNumber}` : maintenanceId.substring(0, 8)
                });
                console.log(`WhatsApp in-progress message sent to customer ${customer.name} for maintenance ${maintenanceId}`);
              } catch (messageError) {
                console.error('Error sending WhatsApp in-progress message:', messageError);
              }
            } else if (status === 'completed') {
              // إرسال رسالة اكتمال الإصلاح
              const { sendNewWhatsAppMessage } = await import('./whatsappService');
              try {
                await sendNewWhatsAppMessage('deviceCompleted', {
                  customerName: customer.name,
                  customerPhone: customer.phone,
                  deviceType: maintenanceData.deviceType || 'غير محدد',
                  cost: maintenanceData.costAfterContact ?
                    `${maintenanceData.costAfterContact} ${settings.currency || 'SAR'}` :
                    `${maintenanceData.finalCost || maintenanceData.initialCost} ${settings.currency || 'SAR'}`,
                  orderId: maintenanceData.orderNumber ? `#${maintenanceData.orderNumber}` : maintenanceId.substring(0, 8)
                });
                console.log(`WhatsApp completion message sent to customer ${customer.name} for maintenance ${maintenanceId}`);
              } catch (messageError) {
                console.error('Error sending WhatsApp completion message:', messageError);
              }
            }
          }
        }
      }
    } catch (whatsappError) {
      // لا نوقف العملية الأساسية في حالة خطأ الواتساب
      console.error('Error sending automatic WhatsApp message:', whatsappError);
    }
  } catch (error) {
    console.error('Error updating maintenance status:', error);
    throw error;
  }
};

// Update maintenance payment (legacy function)
export const updateMaintenancePaymentLegacy = async (
  maintenanceId: string,
  paid: number,
  finalCost: number,
  paymentStatus: PaymentStatus,
  costAfterContact?: number,
  requireCallBeforeMaintenance?: boolean
): Promise<void> => {
  try {
    const maintenanceRef = doc(ensureFirestore(), 'maintenance', maintenanceId);

    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      console.warn(`Maintenance record with ID ${maintenanceId} not found`);
      return;
    }

    const maintenanceData = maintenanceDoc.data();
    if (!maintenanceData) {
      console.warn(`Data for maintenance record with ID ${maintenanceId} is undefined`);
      return;
    }

    const usedParts = maintenanceData.usedParts || [];
    const partsCost = usedParts.reduce((total: number, part: { purchasePrice: number; quantity: number }) =>
      total + (part.purchasePrice * part.quantity), 0);

    const technicianShare = Math.max(0, paid - partsCost);

    const maintenanceFee = Math.max(0, finalCost - partsCost);

    const updateData: any = {
      paid,
      totalPaid: paid, // إضافة totalPaid للتوافق
      finalCost,
      maintenanceFee,
      technicianShare,
      paymentStatus,
      paymentDate: serverTimestamp(), // تحديث تاريخ الدفع
      updatedAt: serverTimestamp()
    };

    // Si se proporciona un costo después de contactar al cliente, actualizarlo
    if (costAfterContact !== undefined) {
      updateData.costAfterContact = costAfterContact;
    }

    // Si se proporciona la opción de contactar antes de la reparación, actualizarla
    if (requireCallBeforeMaintenance !== undefined) {
      updateData.requireCallBeforeMaintenance = requireCallBeforeMaintenance;
    }

    await updateDoc(maintenanceRef, updateData);
  } catch (error) {
    console.error('Error updating maintenance payment:', error);
    throw error;
  }
};

// Remove a part from maintenance record
export const removePartFromMaintenance = async (
  maintenanceId: string,
  partId: string,
  userId?: string
): Promise<void> => {
  console.log('🗑️ بدء إزالة قطعة غيار من الصيانة:', { maintenanceId, partId, userId });
  try {
    const maintenanceRef = doc(ensureFirestore(), 'maintenance', maintenanceId);

    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      console.warn(`Maintenance record with ID ${maintenanceId} not found`);
      return;
    }

    const maintenanceData = maintenanceDoc.data();
    if (!maintenanceData) {
      console.warn(`Data for maintenance record with ID ${maintenanceId} is undefined`);
      return;
    }

    // الحصول على قطع الغيار الحالية
    const currentUsedParts = maintenanceData.usedParts || [];

    // تسجيل قطع الغيار الحالية للتصحيح
    console.log('Current used parts:', currentUsedParts);
    console.log('Part ID to remove:', partId);

    // البحث عن قطعة الغيار المراد حذفها
    const partToRemove = currentUsedParts.find((part: any) => {
      console.log('Checking part:', part);
      return part.id === partId || part.itemId === partId;
    });

    if (!partToRemove) {
      console.warn(`Part with ID ${partId} not found in maintenance record ${maintenanceId}`);
      return;
    }

    console.log('Part to remove found:', partToRemove);

    // تصفية قطعة الغيار المراد حذفها
    const updatedUsedParts = currentUsedParts.filter((part: any) => {
      // التحقق من كل من id و itemId
      return part.id !== partId && part.itemId !== partId;
    });

    console.log('Updated parts after removal:', updatedUsedParts);

    // إعادة حساب تكلفة قطع الغيار
    const partsCost = updatedUsedParts.reduce((total: number, part: { purchasePrice: number; quantity: number }) =>
      total + (part.purchasePrice * part.quantity), 0);

    // إعادة حساب رسوم الصيانة وحصة الفني
    const finalCost = maintenanceData.finalCost || maintenanceData.initialCost;
    const maintenanceFee = Math.max(0, finalCost - partsCost);
    const technicianShare = Math.max(0, maintenanceData.paid - partsCost);

    // تحديث الوثيقة
    await updateDoc(maintenanceRef, {
      usedParts: updatedUsedParts,
      maintenanceFee,
      technicianShare
    });

    // إعادة قطعة الغيار إلى المخزون
    if (partToRemove) {
      console.log('🔄 بدء عملية إرجاع قطعة الغيار للمخزون...');
      try {
        // تحديد معرف المخزن
        const warehouseId = partToRemove.warehouseId || maintenanceData.warehouseId;

        // تحديد معرف العنصر
        const itemId = partToRemove.id || partToRemove.itemId;

        // تحديد الكمية
        const quantity = partToRemove.quantity || 0;

        console.log('📋 معلومات الإرجاع:', {
          itemId,
          warehouseId,
          quantity,
          partName: partToRemove.name
        });

        if (itemId && warehouseId && quantity > 0) {
          console.log(`➕ إرجاع ${quantity} وحدة من ${partToRemove.name} (${itemId}) إلى المخزن ${warehouseId}`);

          // إعادة الكمية إلى المخزن (إضافة الكمية بدلاً من خصمها)
          await updateInventoryQuantityInWarehouse(
            itemId,
            warehouseId,
            quantity, // إضافة الكمية (عكس عملية الخصم)
            userId || 'system',
            `إعادة قطعة غيار إلى المخزون من طلب صيانة رقم ${maintenanceId}`
          );

          console.log(`✅ تم إرجاع ${partToRemove.name} للمخزون بنجاح`);
        } else {
          console.warn('⚠️ معلومات ناقصة لإرجاع القطعة للمخزون:', {
            itemId,
            warehouseId,
            quantity
          });
        }
      } catch (inventoryError) {
        // تسجيل الخطأ ولكن لا نوقف العملية
        console.error('❌ خطأ في إرجاع القطعة للمخزون:', inventoryError);
      }
    }

  } catch (error) {
    console.error('Error removing part from maintenance:', error);
    throw error;
  }
};

// Add used parts to maintenance record
export const addPartsToMaintenance = async (
  maintenanceId: string,
  parts: { itemId: string; quantity: number; name?: string; purchasePrice?: number; sellingPrice?: number }[],
  userId?: string
): Promise<void> => {
  console.log('🔧 بدء إضافة قطع غيار للصيانة:', maintenanceId);
  console.log('📦 قطع الغيار المراد إضافتها:', JSON.stringify(parts, null, 2));

  // Verificar que los precios sean números
  parts.forEach(part => {
    // Verificar precio de compra
    if (typeof part.purchasePrice !== 'number') {
      console.warn(`El precio de compra de ${part.name} no es un número:`, part.purchasePrice);
      part.purchasePrice = Number(part.purchasePrice) || 0;
      console.log(`Precio de compra convertido a número: ${part.purchasePrice}`);
    }

    // Verificar precio de venta
    if (typeof part.sellingPrice !== 'number') {
      console.warn(`El precio de venta de ${part.name} no es un número:`, part.sellingPrice);
      part.sellingPrice = Number(part.sellingPrice) || 0;
      console.log(`Precio de venta convertido a número: ${part.sellingPrice}`);
    }
  });
  try {
    const maintenanceRef = doc(ensureFirestore(), 'maintenance', maintenanceId);

    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      console.warn(`Maintenance record with ID ${maintenanceId} not found`);
      return;
    }

    const maintenanceData = maintenanceDoc.data();
    if (!maintenanceData) {
      console.warn(`Data for maintenance record with ID ${maintenanceId} is undefined`);
      return;
    }

    const currentUsedParts = maintenanceData.usedParts || [];
    console.log('📦 قطع الغيار الحالية قبل الإضافة:', currentUsedParts);
    console.log('📊 عدد قطع الغيار الحالية:', currentUsedParts.length);

    // Procesar las piezas nuevas para asegurarse de que tengan toda la información necesaria
    console.log('Processing parts...');
    const processedParts = await Promise.all(parts.map(async (part) => {
      // Si ya se proporcionó el nombre y los precios, usarlos
      if (part.name && (part.purchasePrice !== undefined || part.sellingPrice !== undefined)) {
        console.log('Using provided name and prices:', part.name, 'Purchase:', part.purchasePrice, 'Selling:', part.sellingPrice);
        // Asegurarse de que los precios sean números
        const purchasePrice = Number(part.purchasePrice) || 0;
        const sellingPrice = Number(part.sellingPrice) || 0;
        console.log('Converted prices to numbers - Purchase:', purchasePrice, 'Selling:', sellingPrice);

        const result = {
          id: part.itemId,
          name: part.name,
          quantity: part.quantity,
          purchasePrice: purchasePrice,
          sellingPrice: sellingPrice
        };
        console.log('Returning processed part with prices:', result);
        return result;
      }

      // Si no, obtener la información del inventario
      try {
        const inventoryRef = doc(ensureFirestore(), 'inventory', part.itemId);
        const inventoryDoc = await getDoc(inventoryRef);

        if (!inventoryDoc.exists()) {
          console.warn(`Inventory item with ID ${part.itemId} not found`);
          return {
            id: part.itemId,
            name: 'Unknown Item',
            quantity: part.quantity,
            purchasePrice: 0
          };
        }

        const itemData = inventoryDoc.data();
        // Asegurarse de que los precios sean números
        const purchasePrice = Number(itemData.purchasePrice || 0);
        const sellingPrice = Number(itemData.sellingPrice || itemData.price || 0);
        console.log('Item data from Firestore:', itemData);
        console.log('Extracted purchase price:', purchasePrice);
        console.log('Extracted selling price:', sellingPrice);

        return {
          id: part.itemId,
          name: itemData.name || 'Unknown Item',
          quantity: part.quantity,
          purchasePrice: purchasePrice,
          sellingPrice: sellingPrice
        };
      } catch (error) {
        console.error(`Error getting inventory item ${part.itemId}:`, error);
        return {
          id: part.itemId,
          name: 'Unknown Item',
          quantity: part.quantity,
          purchasePrice: 0,
          sellingPrice: 0
        };
      }
    }));

    // خصم قطع الغيار من المخزون قبل إضافتها للصيانة
    console.log('Deducting parts from inventory...');
    const deductedParts: Array<{part: any, warehouseId: string}> = [];

    try {
      console.log('🏪 بدء عملية خصم قطع الغيار من المخزون...');

      for (const part of processedParts) {
        // تحديد معرف المخزن (استخدام مخزن الصيانة كافتراضي)
        const warehouseId = maintenanceData.warehouseId;

        console.log(`📦 معالجة قطعة: ${part.name} (${part.id})`);
        console.log(`🏢 معرف المخزن: ${warehouseId}`);

        if (!warehouseId) {
          console.warn(`⚠️ لا يوجد معرف مخزن للصيانة ${maintenanceId}`);
          continue;
        }

        // التحقق من توفر الكمية في المخزن
        console.log(`🔍 التحقق من الكمية المتوفرة في المخزن...`);
        const availableQuantity = await getInventoryQuantityInWarehouse(part.id, warehouseId);
        console.log(`📊 الكمية المتوفرة: ${availableQuantity}, الكمية المطلوبة: ${part.quantity}`);

        if (availableQuantity < part.quantity) {
          const errorMsg = `الكمية المتوفرة (${availableQuantity}) أقل من الكمية المطلوبة (${part.quantity}) للمنتج ${part.name}`;
          console.error(`❌ ${errorMsg}`);
          throw new Error(errorMsg);
        }

        // خصم الكمية من المخزن
        console.log(`➖ خصم ${part.quantity} من المخزن...`);
        await updateInventoryQuantityInWarehouse(
          part.id,
          warehouseId,
          -part.quantity,
          userId || 'system',
          `استخدام في طلب صيانة رقم ${maintenanceId}`
        );
        console.log(`✅ تم خصم ${part.quantity} من ${part.name} بنجاح`);

        // تسجيل القطعة المخصومة للـ rollback المحتمل
        deductedParts.push({ part, warehouseId });

        console.log(`Successfully deducted ${part.quantity} units of ${part.name} from warehouse ${warehouseId}`);
      }
    } catch (inventoryError) {
      console.error(`Error deducting parts from inventory:`, inventoryError);

      // إعادة القطع المخصومة في حالة الخطأ
      console.log('Rolling back inventory changes due to error...');
      for (const { part, warehouseId } of deductedParts) {
        try {
          await updateInventoryQuantityInWarehouse(
            part.id,
            warehouseId,
            part.quantity, // إعادة الكمية
            userId || 'system',
            `إعادة قطعة غيار بسبب خطأ في إضافة قطع الغيار لطلب صيانة رقم ${maintenanceId}`
          );
          console.log(`Rolled back ${part.quantity} units of ${part.name} to warehouse ${warehouseId}`);
        } catch (rollbackError) {
          console.error(`Error rolling back part ${part.name}:`, rollbackError);
        }
      }

      throw new Error(`فشل في خصم قطع الغيار من المخزون: ${inventoryError}`);
    }

    const updatedUsedParts = [...currentUsedParts, ...processedParts];
    console.log('📋 قطع الغيار المحدثة (قبل الحفظ):', updatedUsedParts);
    console.log('📊 العدد الإجمالي لقطع الغيار:', updatedUsedParts.length);

    // التأكد من أن كل قطعة لها معرف فريد
    updatedUsedParts.forEach((part, index) => {
      if (!part.id && !part.itemId) {
        console.warn(`⚠️ قطعة غيار بدون معرف في الفهرس ${index}:`, part);
      }
    });

    const partsCost = updatedUsedParts.reduce((total: number, part: { purchasePrice: number; sellingPrice: number; quantity: number }) => {
      console.log('Calculating cost for part:', part);
      // استخدام سعر البيع لحساب إجمالي تكلفة قطع الغيار للعميل
      const sellingPrice = Number(part.sellingPrice) || 0;
      const quantity = Number(part.quantity) || 0;
      const cost = sellingPrice * quantity;
      console.log(`Part cost (selling price): ${sellingPrice} * ${quantity} = ${cost}`);
      return total + cost;
    }, 0);
    console.log('Total parts cost:', partsCost);

    const finalCost = maintenanceData.finalCost || maintenanceData.initialCost;
    const maintenanceFee = Math.max(0, finalCost - partsCost);
    const technicianShare = Math.max(0, maintenanceData.paid - partsCost);

    // Asegurarse de que los datos sean correctos antes de guardarlos
    const sanitizedParts = updatedUsedParts.map((part, index) => {
      const sanitized = {
        id: part.id || part.itemId || `temp_${Date.now()}_${index}`,
        itemId: part.itemId || part.id,
        name: part.name || 'Unknown Item',
        quantity: Number(part.quantity) || 0,
        purchasePrice: Number(part.purchasePrice) || 0,
        sellingPrice: Number(part.sellingPrice) || 0
      };

      console.log(`🧹 تنظيف قطعة ${index + 1}:`, {
        original: part,
        sanitized: sanitized
      });

      return sanitized;
    });

    console.log('✨ قطع الغيار المنظفة النهائية:', sanitizedParts);

    const updateData = {
      usedParts: sanitizedParts,
      maintenanceFee,
      technicianShare
    };

    console.log('📝 تحديث طلب الصيانة بالبيانات المنظفة:', updateData);
    console.log('🔧 قطع الغيار المحدثة للحفظ:', sanitizedParts);

    try {
      console.log('💾 بدء حفظ البيانات في Firestore...');
      await updateDoc(maintenanceRef, updateData);
      console.log('✅ تم حفظ البيانات في Firestore بنجاح');

      // التحقق من الحفظ
      console.log('🔍 التحقق من الحفظ...');
      const updatedDoc = await getDoc(maintenanceRef);
      if (updatedDoc.exists()) {
        const updatedData = updatedDoc.data();
        console.log('📋 البيانات المحفوظة فعلياً:', {
          usedPartsCount: updatedData.usedParts ? updatedData.usedParts.length : 0,
          usedParts: updatedData.usedParts
        });
      }
    } catch (updateError) {
      console.error('Error updating maintenance document:', updateError);

      // إعادة القطع المخصومة في حالة فشل تحديث قاعدة البيانات
      console.log('Rolling back inventory changes due to database update error...');
      for (const { part, warehouseId } of deductedParts) {
        try {
          await updateInventoryQuantityInWarehouse(
            part.id,
            warehouseId,
            part.quantity, // إعادة الكمية
            userId || 'system',
            `إعادة قطعة غيار بسبب خطأ في تحديث طلب صيانة رقم ${maintenanceId}`
          );
          console.log(`Rolled back ${part.quantity} units of ${part.name} to warehouse ${warehouseId}`);
        } catch (rollbackError) {
          console.error(`Error rolling back part ${part.name}:`, rollbackError);
        }
      }

      throw new Error(`فشل في تحديث طلب الصيانة: ${updateError}`);
    }
  } catch (error) {
    console.error('Error adding parts to maintenance:', error);
    throw error;
  }
};

// Delete maintenance record
export const deleteMaintenanceRecord = async (recordId: string, userId?: string): Promise<void> => {
  try {
    const recordRef = doc(ensureFirestore(), 'maintenance', recordId);

    // الحصول على بيانات طلب الصيانة قبل حذفه
    const maintenanceDoc = await getDoc(recordRef);

    if (maintenanceDoc.exists()) {
      const maintenanceData = maintenanceDoc.data();
      const usedParts = maintenanceData.usedParts || [];

      // إعادة قطع الغيار إلى المخزون قبل حذف الطلب
      if (usedParts.length > 0) {
        console.log(`Returning ${usedParts.length} parts to inventory before deleting maintenance record ${recordId}`);

        for (const part of usedParts) {
          try {
            // تحديد معرف المخزن
            const warehouseId = part.warehouseId || maintenanceData.warehouseId;

            // تحديد معرف العنصر
            const itemId = part.id || part.itemId;

            // تحديد الكمية
            const quantity = part.quantity || 0;

            if (itemId && warehouseId && quantity > 0) {
              console.log(`Returning ${quantity} units of item ${itemId} to warehouse ${warehouseId}`);

              // إعادة الكمية إلى المخزون
              await updateInventoryQuantityInWarehouse(
                itemId,
                warehouseId,
                quantity, // إضافة الكمية (عكس عملية الخصم)
                userId || 'system',
                `إعادة قطعة غيار إلى المخزون بسبب حذف طلب صيانة رقم ${recordId}`
              );
            }
          } catch (inventoryError) {
            // تسجيل الخطأ ولكن لا نوقف العملية
            console.error(`Error returning part ${part.id || part.itemId} to inventory:`, inventoryError);
          }
        }
      }
    }

    // حذف طلب الصيانة
    await deleteDoc(recordRef);
    console.log(`Maintenance record ${recordId} deleted successfully`);
  } catch (error) {
    console.error(`Error deleting maintenance record with ID ${recordId}:`, error);
    throw error;
  }
};

// Get all maintenance records for a branch
export const getMaintenanceByBranchId = async (branchId: string): Promise<MaintenanceRecord[]> => {
  try {
    const maintenanceCollection = getMaintenanceCollection();
    const q = query(
      maintenanceCollection,
      where('branchId', '==', branchId),
      orderBy('receivedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const records: MaintenanceRecord[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      records.push(mapFirestoreDataToMaintenanceRecord(doc.id, data));
    });

    return records;
  } catch (error) {
    console.error('Error getting maintenance records by branch ID:', error);
    return [];
  }
};

// Check if user has access to maintenance record
export const checkUserMaintenanceAccess = async (userId: string, maintenanceId: string): Promise<boolean> => {
  try {
    const user = await getUserById(userId);
    if (!user) {
      return false;
    }

    // Admin, branch manager and warehouse manager have access to all maintenance records
    if (user.role === UserRole.ADMIN || user.role === UserRole.BRANCH_MANAGER || user.role === UserRole.WAREHOUSE_MANAGER) {
      return true;
    }

    const maintenance = await getMaintenanceById(maintenanceId);
    if (!maintenance) {
      return false;
    }

    // Check if user belongs to the same branch
    if (user.branchId && maintenance.branchId === user.branchId) {
      return true;
    }

    // Check if user created or received the maintenance
    if (maintenance.receivedByEmployeeId === userId) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error checking user maintenance access:', error);
    return false;
  }
};

// Add parts to maintenance from specific warehouse
export const addPartsToMaintenanceFromWarehouse = async (
  maintenanceId: string,
  parts: MaintenancePart[],
  userId: string
): Promise<void> => {
  try {
    // التحقق من صلاحية المستخدم
    const hasAccess = await checkUserMaintenanceAccess(userId, maintenanceId);
    if (!hasAccess) {
      throw new Error('ليس لديك صلاحية لإضافة قطع غيار لهذا الطلب');
    }

    const maintenanceRef = doc(ensureFirestore(), 'maintenance', maintenanceId);
    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      throw new Error('سجل الصيانة غير موجود');
    }

    const maintenanceData = maintenanceDoc.data() as MaintenanceRecord;

    // الحصول على القطع الحالية
    const currentParts = maintenanceData.usedParts || [];
    const newPartsArray = [...currentParts];

    // حساب التكلفة الإجمالية للقطع الجديدة
    let newPartsCost = 0;

    for (const part of parts) {
      // التحقق من المخزن
      if (!part.warehouseId) {
        // استخدام المخزن المرتبط بالصيانة إذا لم يكن محددًا
        part.warehouseId = maintenanceData.warehouseId;
      }

      newPartsCost += part.total;

      // خصم الكمية من المخزن
      await updateInventoryQuantityInWarehouse(
        part.itemId,
        part.warehouseId,
        -part.quantity,
        userId,
        `استخدام في طلب صيانة رقم ${maintenanceId}`
      );

      // تحويل القطعة إلى كائن InventoryItem قبل إضافته إلى المصفوفة
      const inventoryItem: InventoryItem = {
        id: part.itemId,
        name: part.name,
        quantity: part.quantity,
        sellingPrice: part.price,
        purchasePrice: part.price,
        sku: '',
        category: '',
        minQuantity: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        warehouseId: part.warehouseId,
        warehouseName: part.warehouseName
      };

      // إضافة خاصية total كخاصية مخصصة
      (inventoryItem as any).total = part.total;

      // إضافة القطعة إلى المصفوفة
      newPartsArray.push(inventoryItem);
    }

    // حساب التكلفة الإجمالية الجديدة
    const newTotalPartsCost = currentParts.reduce((sum, part: any) => sum + (part.total || 0), 0) + newPartsCost;

    // حساب رسوم الصيانة الجديدة (التكلفة الأولية - تكلفة القطع)
    const newMaintenanceFee = maintenanceData.initialCost - newTotalPartsCost;

    // حساب حصة الفني الجديدة (المدفوع - تكلفة القطع)
    const newTechShare = maintenanceData.paid - newTotalPartsCost > 0 ? maintenanceData.paid - newTotalPartsCost : 0;

    // تحديث سجل الصيانة
    await updateDoc(maintenanceRef, {
      usedParts: newPartsArray,
      maintenanceFee: newMaintenanceFee,
      technicianShare: newTechShare
    });
  } catch (error) {
    console.error('Error adding parts to maintenance:', error);
    throw error;
  }
};

// Get maintenance records by warehouse
export const getMaintenanceByWarehouseId = async (warehouseId: string): Promise<MaintenanceRecord[]> => {
  try {
    const maintenanceCollection = getMaintenanceCollection();
    const q = query(
      maintenanceCollection,
      where('warehouseId', '==', warehouseId),
      orderBy('receivedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const records: MaintenanceRecord[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      records.push(mapFirestoreDataToMaintenanceRecord(doc.id, data));
    });

    return records;
  } catch (error) {
    console.error('Error getting maintenance records by warehouse ID:', error);
    return [];
  }
};

// فحص عدد الطلبات التي تحتاج إصلاح (بدون إصلاح فعلي)
export const checkRecordsNeedingFix = async (): Promise<number> => {
  try {
    const maintenanceCollection = getMaintenanceCollection();
    const q = query(maintenanceCollection, where('status', '==', 'delivered'));
    const querySnapshot = await getDocs(q);
    
    let needingFixCount = 0;
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (!data.deliveredAt) {
        needingFixCount++;
      }
    });
    
    return needingFixCount;
  } catch (error) {
    console.error('❌ خطأ في فحص الطلبات:', error);
    return 0;
  }
};

// إصلاح الطلبات القديمة التي ليس لها deliveredAt
export const fixOldDeliveredRecords = async (): Promise<{fixed: number, alreadyFixed: number}> => {
  try {
    console.log('🔧 بدء إصلاح الطلبات المُسلمة القديمة التي ليس لها deliveredAt...');
    
    const maintenanceCollection = getMaintenanceCollection();
    
    // جلب جميع الطلبات المُسلمة
    const q = query(
      maintenanceCollection,
      where('status', '==', 'delivered')
    );

    const querySnapshot = await getDocs(q);
    let fixedCount = 0;
    let alreadyFixedCount = 0;

    console.log(`📊 تم جلب ${querySnapshot.docs.length} طلب مُسلم`);

    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();
      const recordId = docSnap.id;
      
      if (!data.deliveredAt) {
        // طلب مُسلم لكن ليس له deliveredAt
        console.log(`🔧 إصلاح طلب ${recordId}: ليس له deliveredAt`);
        
        // استخدام actualDeliveryDate إذا وُجد، أو updatedAt، أو اليوم
        const deliveredAt = data.actualDeliveryDate?.toDate() || 
                           data.updatedAt?.toDate() || 
                           new Date();
        
        // تحديث الطلب
        await updateDoc(doc(ensureFirestore(), 'maintenance', recordId), {
          deliveredAt: deliveredAt
        });
        
        console.log(`✅ تم إصلاح طلب ${recordId}: deliveredAt = ${deliveredAt.toISOString().split('T')[0]}`);
        fixedCount++;
      } else {
        console.log(`✅ طلب ${recordId} مُصحح مسبقاً: deliveredAt = ${data.deliveredAt.toDate().toISOString().split('T')[0]}`);
        alreadyFixedCount++;
      }
    }

    console.log(`🎉 انتهى الإصلاح: ${fixedCount} طلب تم إصلاحه، ${alreadyFixedCount} طلب مُصحح مسبقاً`);
    
    return { fixed: fixedCount, alreadyFixed: alreadyFixedCount };
  } catch (error) {
    console.error('❌ خطأ في إصلاح الطلبات القديمة:', error);
    throw error;
  }
};

// Get maintenance records by delivery date range
export const getMaintenanceByDeliveryDateRange = async (
  startDate: Date,
  endDate: Date,
  branchId?: string
): Promise<MaintenanceRecord[]> => {
  try {
    const maintenanceCollection = getMaintenanceCollection();

    // حل مؤقت: استعلام مبسط ثم تصفية محلية لتجنب فهارس Firebase المركبة
    console.log(`🔧 جلب طلبات الصيانة المُسلمة من ${startDate.toISOString().split('T')[0]} إلى ${endDate.toISOString().split('T')[0]}`);
    
    let q;
    if (branchId) {
      q = query(
        maintenanceCollection,
        where('status', '==', 'delivered'),
        where('branchId', '==', branchId)
      );
    } else {
      q = query(
        maintenanceCollection,
        where('status', '==', 'delivered')
      );
    }

    const querySnapshot = await getDocs(q);
    const allRecords: MaintenanceRecord[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      allRecords.push(mapFirestoreDataToMaintenanceRecord(doc.id, data));
    });

    // تصفية محلية بناءً على deliveredAt
    const records = allRecords.filter(record => {
      if (!record.deliveredAt) {
        console.log(`⚠️ طلب ${record.id} لا يحتوي على deliveredAt`);
        return false;
      }
      
      const deliveredDate = new Date(record.deliveredAt);
      const isInRange = deliveredDate >= startDate && deliveredDate <= endDate;
      
      if (isInRange) {
        console.log(`✅ طلب ${record.id} - مُسلم ${deliveredDate.toISOString().split('T')[0]} - مشمول`);
      } else {
        console.log(`❌ طلب ${record.id} - مُسلم ${deliveredDate.toISOString().split('T')[0]} - خارج النطاق`);
      }
      
      return isInRange;
    });

    // ترتيب النتائج حسب تاريخ التسليم (الأحدث أولاً)
    records.sort((a, b) => {
      const dateA = new Date(a.deliveredAt || 0);
      const dateB = new Date(b.deliveredAt || 0);
      return dateB.getTime() - dateA.getTime();
    });

    console.log(`📊 تم تصفية ${allRecords.length} طلب → ${records.length} طلب مُسلم في الفترة المطلوبة`);

    return records;
  } catch (error) {
    console.error('Error getting maintenance records by delivery date range:', error);
    return [];
  }
};

// Get maintenance records delivered today
export const getMaintenanceDeliveredToday = async (branchId?: string): Promise<MaintenanceRecord[]> => {
  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

  return getMaintenanceByDeliveryDateRange(startOfDay, endOfDay, branchId);
};

// Get maintenance records delivered yesterday
export const getMaintenanceDeliveredYesterday = async (branchId?: string): Promise<MaintenanceRecord[]> => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const startOfYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  const endOfYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);

  return getMaintenanceByDeliveryDateRange(startOfYesterday, endOfYesterday, branchId);
};

// Get maintenance records delivered this month
export const getMaintenanceDeliveredThisMonth = async (branchId?: string): Promise<MaintenanceRecord[]> => {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

  return getMaintenanceByDeliveryDateRange(startOfMonth, endOfMonth, branchId);
};

// Get maintenance records by date range and optional branch
export const getMaintenanceByDateRangeAndBranch = async (
  startDate: Date,
  endDate: Date,
  branchId?: string
): Promise<MaintenanceRecord[]> => {
  try {
    const maintenanceCollection = getMaintenanceCollection();

    // Create query constraints
    const constraints = [
      where('receivedAt', '>=', startDate),
      where('receivedAt', '<=', endDate),
      orderBy('receivedAt', 'desc')
    ];

    // Add branch filter if provided
    let q;
    if (branchId) {
      q = query(
        maintenanceCollection,
        where('branchId', '==', branchId),
        ...constraints
      );
    } else {
      q = query(
        maintenanceCollection,
        ...constraints
      );
    }

    const querySnapshot = await getDocs(q);
    const records: MaintenanceRecord[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      records.push(mapFirestoreDataToMaintenanceRecord(doc.id, data));
    });

    return records;
  } catch (error) {
    console.error('Error getting maintenance records by date range and branch:', error);
    return [];
  }
};

// Get maintenance records by device ID
export const getMaintenanceByDeviceId = async (deviceId: string): Promise<MaintenanceRecord[]> => {
  try {
    console.log('Getting maintenance records for device ID:', deviceId);

    const maintenanceCollection = getMaintenanceCollection();
    let records: MaintenanceRecord[] = [];

    // الحصول على معلومات الجهاز أولاً
    const device = await getDeviceById(deviceId);
    console.log('Device details:', device);

    if (!device) {
      console.log('Device not found with ID:', deviceId);
      return [];
    }

    // البحث في جميع سجلات الصيانة للعميل (بدون ترتيب لتجنب الحاجة للفهرس)
    const customerMaintenanceQuery = query(
      maintenanceCollection,
      where('customerId', '==', device.customerId)
    );

    let customerMaintenanceSnapshot;

    try {
      customerMaintenanceSnapshot = await getDocs(customerMaintenanceQuery);
      console.log(`Found ${customerMaintenanceSnapshot.size} maintenance records for customer ${device.customerId}`);
    } catch (indexError: any) {
      console.log('Index error, falling back to search all maintenance records:', indexError?.message || indexError);

      // البحث في جميع سجلات الصيانة كحل بديل
      const allMaintenanceQuery = query(maintenanceCollection);
      customerMaintenanceSnapshot = await getDocs(allMaintenanceQuery);
      console.log(`Searching in ${customerMaintenanceSnapshot.size} total maintenance records`);
    }

    customerMaintenanceSnapshot.forEach(doc => {
      const data = doc.data();
      let isMatch = false;
      let matchReason = '';

      // إذا كنا نبحث في جميع السجلات، تأكد أن السجل ينتمي للعميل الصحيح
      if (data.customerId !== device.customerId) {
        return; // تخطي السجلات التي لا تنتمي للعميل
      }

      console.log(`Checking maintenance record ${doc.id}:`, {
        deviceId: data.deviceId,
        deviceType: data.deviceType,
        brand: data.brand,
        model: data.model,
        serialNumber: data.serialNumber,
        devices: data.devices
      });

      // التحقق من الحقل deviceId المباشر
      if (data.deviceId === deviceId) {
        console.log('✅ Found match by deviceId:', doc.id);
        isMatch = true;
        matchReason = 'deviceId';
      }

      // التحقق من مصفوفة الأجهزة
      if (!isMatch && data.devices && Array.isArray(data.devices)) {
        const hasDevice = data.devices.some((d: any) => d.deviceId === deviceId);
        if (hasDevice) {
          console.log('✅ Found match in devices array:', doc.id);
          isMatch = true;
          matchReason = 'devices array';
        }
      }

      // التحقق من تطابق تفاصيل الجهاز (للسجلات القديمة)
      if (!isMatch) {
        const typeMatch = data.deviceType === device.type;
        const brandMatch = device.brand && data.brand && data.brand.toLowerCase().includes(device.brand.toLowerCase());
        const modelMatch = device.model && data.model && data.model.toLowerCase().includes(device.model.toLowerCase());
        const serialMatch = device.serialNumber && data.serialNumber === device.serialNumber;

        console.log('Checking device details match:', {
          deviceType: { device: device.type, maintenance: data.deviceType, match: typeMatch },
          brand: { device: device.brand, maintenance: data.brand, match: brandMatch },
          model: { device: device.model, maintenance: data.model, match: modelMatch },
          serial: { device: device.serialNumber, maintenance: data.serialNumber, match: serialMatch }
        });

        // إذا تطابق النوع والماركة (حتى لو كان الموديل مختلف قليلاً)
        if (typeMatch && brandMatch) {
          console.log('✅ Found match by device details (type + brand):', doc.id);
          isMatch = true;
          matchReason = 'type + brand';
        }
        // أو إذا تطابق النوع والرقم التسلسلي
        else if (typeMatch && serialMatch) {
          console.log('✅ Found match by device details (type + serial):', doc.id);
          isMatch = true;
          matchReason = 'type + serial';
        }
        // أو إذا تطابق النوع والموديل والماركة
        else if (typeMatch && brandMatch && modelMatch) {
          console.log('✅ Found match by device details (type + brand + model):', doc.id);
          isMatch = true;
          matchReason = 'type + brand + model';
        }
      }

      if (isMatch) {
        console.log(`✅ Adding maintenance record ${doc.id} (matched by: ${matchReason})`);
        records.push(mapFirestoreDataToMaintenanceRecord(doc.id, data));
      } else {
        console.log(`❌ No match for maintenance record ${doc.id}`);
      }
    });

    console.log(`Found ${records.length} maintenance records for device ${deviceId}`);

    // ترتيب النتائج حسب تاريخ الاستلام (الأحدث أولاً)
    records.sort((a, b) => {
      const dateA = new Date(a.receivedAt);
      const dateB = new Date(b.receivedAt);
      return dateB.getTime() - dateA.getTime();
    });

    return records;
  } catch (error) {
    console.error('Error getting maintenance records by device ID:', error);
    return [];
  }
};

// واجهة بيانات المرتجع
export interface MaintenanceRefund {
  id: string;
  maintenanceId: string;
  refundAmount: number;
  refundReason: string;
  refundMethod: 'cash' | 'debt_reduction';
  notes?: string;
  employeeId: string;
  employeeName: string;
  customerId: string;
  customerName: string;
  deviceType: string;
  deviceSize: string;
  createdAt: Date | string;
  processedAt: Date | string;
}

// معالجة مرتجع الصيانة
export const processMaintenanceRefund = async (
  refundData: {
    maintenanceId: string;
    refundAmount: number;
    refundReason: string;
    refundMethod: 'cash' | 'debt_reduction';
    notes?: string;
    employeeId: string;
    employeeName: string;
  }
): Promise<MaintenanceRefund> => {
  try {
    console.log('🔄 بدء معالجة مرتجع الصيانة:', refundData);

    const firestore = ensureFirestore();
    console.log('✅ تم التأكد من توفر Firestore');

    // الحصول على بيانات طلب الصيانة
    console.log('📋 جلب بيانات طلب الصيانة:', refundData.maintenanceId);
    const maintenanceRef = doc(firestore, 'maintenance', refundData.maintenanceId);
    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      console.error('❌ طلب الصيانة غير موجود:', refundData.maintenanceId);
      throw new Error('طلب الصيانة غير موجود');
    }

    const maintenance = maintenanceDoc.data() as MaintenanceRecord;
    console.log('✅ تم جلب بيانات طلب الصيانة:', {
      id: refundData.maintenanceId,
      totalPaid: maintenance.totalPaid,
      customerName: maintenance.customerName,
      deviceType: maintenance.deviceType
    });

    // التحقق من أن المبلغ المرتجع لا يتجاوز المبلغ المدفوع
    if (refundData.refundAmount > (maintenance.totalPaid || 0)) {
      throw new Error('مبلغ الإرجاع أكبر من المبلغ المدفوع');
    }

    // إنشاء سجل المرتجع
    const refundRecord: Omit<MaintenanceRefund, 'id'> = {
      maintenanceId: refundData.maintenanceId,
      refundAmount: refundData.refundAmount,
      refundReason: refundData.refundReason,
      refundMethod: refundData.refundMethod,
      notes: refundData.notes,
      employeeId: refundData.employeeId,
      employeeName: refundData.employeeName,
      customerId: maintenance.customerId,
      customerName: maintenance.customerName,
      deviceType: maintenance.deviceType,
      deviceSize: maintenance.devices?.[0]?.size || 'غير محدد',
      createdAt: new Date(),
      processedAt: new Date()
    };

    // إضافة سجل المرتجع إلى قاعدة البيانات
    console.log('💾 إنشاء سجل المرتجع في قاعدة البيانات...');
    const refundsCollection = collection(firestore, 'maintenanceRefunds');
    const refundDocRef = await addDoc(refundsCollection, refundRecord);
    console.log('✅ تم إنشاء سجل المرتجع بنجاح، ID:', refundDocRef.id);

    // تحديث طلب الصيانة
    console.log('🔄 تحديث طلب الصيانة...');
    const newPaidAmount = (maintenance.totalPaid || 0) - refundData.refundAmount;
    const newPaymentStatus: PaymentStatus = newPaidAmount <= 0 ? 'unpaid' :
                                          newPaidAmount >= (maintenance.finalCost || maintenance.initialCost) ? 'fullyPaid' : 'partiallyPaid';

    console.log('💰 تحديث المبالغ:', {
      oldPaidAmount: maintenance.totalPaid || 0,
      refundAmount: refundData.refundAmount,
      newPaidAmount,
      newPaymentStatus
    });

    await updateDoc(maintenanceRef, {
      totalPaid: newPaidAmount,
      paymentStatus: newPaymentStatus,
      updatedAt: serverTimestamp()
    });

    console.log('✅ تم تحديث طلب الصيانة بنجاح');

    // معالجة تأثير الإرجاع حسب الطريقة
    if (refundData.refundMethod === 'cash') {
      // خصم من الدرج النقدي
      await addCashDrawerTransaction({
        type: 'maintenance_refund',
        amount: -refundData.refundAmount, // مبلغ سالب للخصم من الخزينة
        description: `إرجاع مبلغ صيانة - ${maintenance.deviceType} ${maintenance.devices?.[0]?.size || 'غير محدد'}`,
        maintenanceId: refundData.maintenanceId,
        customerId: maintenance.customerId,
        customerName: maintenance.customerName,
        employeeId: refundData.employeeId,
        employeeName: refundData.employeeName,
        branchId: maintenance.branchId,
        notes: `إرجاع مبلغ صيانة للعميل ${maintenance.customerName}`
      });
    } else if (refundData.refundMethod === 'debt_reduction') {
      // تقليل دين العميل
      const customerRef = doc(firestore, 'customers', maintenance.customerId);
      const customerDoc = await getDoc(customerRef);

      if (customerDoc.exists()) {
        const currentDebt = customerDoc.data().debt || 0;
        const newDebt = Math.max(0, currentDebt - refundData.refundAmount);

        await updateDoc(customerRef, {
          debt: newDebt,
          updatedAt: serverTimestamp()
        });
      }
    }

    // إرجاع بيانات المرتجع
    return {
      id: refundDocRef.id,
      ...refundRecord,
      createdAt: new Date(),
      processedAt: new Date()
    } as MaintenanceRefund;

  } catch (error) {
    console.error('Error processing maintenance refund:', error);
    throw error;
  }
};

// إضافة معاملة للدرج النقدي (دالة محدثة ومُحسنة)
export const addCashDrawerTransaction = async (transactionData: {
  type: string;
  amount: number;
  description: string;
  maintenanceId?: string;
  customerId?: string;
  customerName?: string;
  employeeId?: string;
  employeeName?: string;
  branchId?: string;
  refundId?: string;
  notes?: string;
}) => {
  try {
    console.log('💰 بدء إضافة معاملة الدرج النقدي:', transactionData);

    const firestore = ensureFirestore();
    const drawerCollection = collection(firestore, 'drawerTransactions');

    const transactionDoc = {
      type: transactionData.type,
      amount: transactionData.amount, // يجب أن يكون موجباً للإضافة، سالباً للخصم
      description: transactionData.description,
      ...(transactionData.maintenanceId && { maintenanceId: transactionData.maintenanceId }),
      ...(transactionData.customerId && { customerId: transactionData.customerId }),
      ...(transactionData.customerName && { customerName: transactionData.customerName }),
      ...(transactionData.employeeId && { employeeId: transactionData.employeeId }),
      ...(transactionData.employeeName && { employeeName: transactionData.employeeName }),
      ...(transactionData.branchId && { branchId: transactionData.branchId }),
      ...(transactionData.refundId && { refundId: transactionData.refundId }),
      ...(transactionData.notes && { notes: transactionData.notes }),
      createdAt: serverTimestamp(),
      date: new Date().toISOString().split('T')[0] // تاريخ اليوم
    };

    console.log('📄 بيانات المعاملة التي سيتم حفظها:', transactionDoc);

    const docRef = await addDoc(drawerCollection, transactionDoc);

    console.log('✅ تم إضافة معاملة الدرج النقدي بنجاح، ID:', docRef.id);
    console.log(`💰 المبلغ: ${transactionData.amount} - ${transactionData.amount > 0 ? 'إضافة إلى الخزينة' : 'خصم من الخزينة'}`);
  } catch (error) {
    console.error('❌ خطأ في إضافة معاملة الدرج النقدي:', error);

    // تفاصيل أكثر عن الخطأ
    if (error instanceof Error) {
      console.error('❌ رسالة الخطأ:', error.message);
      console.error('❌ تفاصيل الخطأ:', error.stack);
    }

    throw error;
  }
};

// الحصول على مرتجعات الصيانة
export const getMaintenanceRefunds = async (maintenanceId?: string): Promise<MaintenanceRefund[]> => {
  try {
    const firestore = ensureFirestore();
    const refundsCollection = collection(firestore, 'maintenanceRefunds');

    let q = query(refundsCollection, orderBy('createdAt', 'desc'));

    if (maintenanceId) {
      q = query(refundsCollection, where('maintenanceId', '==', maintenanceId), orderBy('createdAt', 'desc'));
    }

    const querySnapshot = await getDocs(q);
    const refunds: MaintenanceRefund[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      refunds.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        processedAt: data.processedAt?.toDate() || new Date()
      } as MaintenanceRefund);
    });

    return refunds;
  } catch (error) {
    console.error('Error getting maintenance refunds:', error);
    return [];
  }
};

// Search maintenance records with enhanced functionality
export const searchMaintenanceRecords = async (searchTerm: string): Promise<MaintenanceRecord[]> => {
  try {
    // إذا كان مصطلح البحث فارغًا، إرجاع مصفوفة فارغة
    if (!searchTerm || searchTerm.trim() === '') {
      return [];
    }

    console.log('🔍 البحث عن:', searchTerm);

    // محاولة استخدام البيانات المخزنة محليًا للبحث
    const cachedData = localStorage.getItem('maintenance_cache');
    if (cachedData) {
      try {
        const maintenanceRecords = JSON.parse(cachedData);
        const searchTermLower = searchTerm.toLowerCase().trim();
        const isPhoneSearch = /^\d+$/.test(searchTerm);

        console.log('🔍 البحث في البيانات المخزنة محلياً...');
        console.log('📞 هل هو بحث برقم هاتف؟', isPhoneSearch);

        // البحث في البيانات المخزنة محليًا مع دعم البحث برقم الهاتف
        const results = maintenanceRecords.filter((record: MaintenanceRecord) => {
          // البحث في اسم العميل
          const nameMatch = record.customerName?.toLowerCase().includes(searchTermLower);

          // البحث في رقم الطلب
          const orderMatch = record.orderNumber?.toString().includes(searchTerm);

          // البحث في المشكلة
          const issueMatch = record.issue?.toLowerCase().includes(searchTermLower);

          // البحث في نوع الجهاز
          const deviceMatch = record.deviceType?.toLowerCase().includes(searchTermLower);

          // البحث في رقم الهاتف (من بيانات العميل المخزنة)
          let phoneMatch = false;
          if (isPhoneSearch) {
            // محاولة الحصول على رقم الهاتف من البيانات المخزنة للعملاء
            try {
              const customersData = localStorage.getItem('customers_cache');
              if (customersData) {
                const customers = JSON.parse(customersData);
                const customer = customers.find((c: any) => c.id === record.customerId);
                if (customer && customer.phone) {
                  const cleanPhone = customer.phone.replace(/\D/g, ''); // إزالة كل شيء عدا الأرقام
                  const cleanSearch = searchTerm.replace(/\D/g, '');

                  // البحث الجزئي في رقم الهاتف
                  phoneMatch = cleanPhone.includes(cleanSearch) ||
                              cleanPhone.startsWith(cleanSearch) ||
                              cleanPhone.endsWith(cleanSearch);
                }
              }
            } catch (error) {
              console.warn('خطأ في البحث برقم الهاتف في البيانات المخزنة:', error);
            }
          }

          // البحث في التشخيص
          const diagnosisMatch = record.diagnosis?.toLowerCase().includes(searchTermLower);

          // البحث في اسم الفني
          const technicianMatch = record.technicianName?.toLowerCase().includes(searchTermLower);

          return nameMatch || orderMatch || issueMatch || deviceMatch || phoneMatch || diagnosisMatch || technicianMatch;
        });

        if (results.length > 0) {
          console.log(`✅ تم العثور على ${results.length} نتيجة في البيانات المخزنة محلياً`);
          return results;
        } else {
          console.log('❌ لم يتم العثور على نتائج في البيانات المخزنة محلياً');
        }
      } catch (cacheError) {
        console.warn('Error searching in cached maintenance data:', cacheError);
      }
    }

    // إذا لم تكن هناك بيانات مخزنة أو لم يتم العثور على نتائج، البحث في قاعدة البيانات
    console.log('🔍 البحث في قاعدة البيانات...');
    const maintenanceCollection = getMaintenanceCollection();
    const isPhoneSearch = /^\d+$/.test(searchTerm);
    const searchTermLower = searchTerm.toLowerCase().trim();

    // البحث في عدة حقول
    const queries = [];

    // البحث في اسم العميل
    queries.push(
      query(
        maintenanceCollection,
        where('customerName', '>=', searchTermLower),
        where('customerName', '<=', searchTermLower + '\uf8ff')
      )
    );

    // البحث في رقم الطلب (إذا كان رقم)
    if (/^\d+$/.test(searchTerm)) {
      const orderNumber = parseInt(searchTerm);
      if (!isNaN(orderNumber)) {
        queries.push(
          query(
            maintenanceCollection,
            where('orderNumber', '==', orderNumber)
          )
        );
      }
    }

    // البحث في نوع الجهاز
    queries.push(
      query(
        maintenanceCollection,
        where('deviceType', '>=', searchTermLower),
        where('deviceType', '<=', searchTermLower + '\uf8ff')
      )
    );

    // البحث في المشكلة
    queries.push(
      query(
        maintenanceCollection,
        where('issue', '>=', searchTermLower),
        where('issue', '<=', searchTermLower + '\uf8ff')
      )
    );

    // ملاحظة: البحث برقم الهاتف يتم من خلال البيانات المخزنة محلياً
    // لأن رقم الهاتف مخزن في جدول العملاء وليس في جدول الصيانة مباشرة
    if (isPhoneSearch) {
      console.log('📞 البحث برقم الهاتف سيتم من خلال البيانات المخزنة محلياً أو البحث الشامل...');
    }

    console.log(`🔍 تنفيذ ${queries.length} استعلام بحث...`);
    const queryResults = await Promise.all(queries.map(q => getDocs(q).catch(err => {
      console.warn('خطأ في استعلام البحث:', err);
      return { docs: [] };
    })));

    // دمج النتائج وإزالة التكرار
    const resultsMap = new Map<string, MaintenanceRecord>();

    queryResults.forEach((snapshot, index) => {
      console.log(`📊 الاستعلام ${index + 1}: ${snapshot.docs.length} نتيجة`);
      snapshot.docs.forEach(doc => {
        if (!resultsMap.has(doc.id)) {
          const data = doc.data();
          resultsMap.set(doc.id, mapFirestoreDataToMaintenanceRecord(doc.id, data));
        }
      });
    });

    const finalResults = Array.from(resultsMap.values());

    // إذا كان البحث برقم هاتف ولم نجد نتائج، جرب البحث الشامل مع ربط بيانات العملاء
    if (isPhoneSearch && finalResults.length === 0) {
      console.log('📞 لم يتم العثور على نتائج بالبحث المباشر، جاري البحث الشامل مع ربط بيانات العملاء...');

      try {
        // الحصول على جميع العملاء أولاً
        const { getAllCustomers } = await import('./customerService');
        const customers = await getAllCustomers();

        // البحث عن العملاء الذين يطابق رقم هاتفهم البحث
        const matchingCustomers = customers.filter(customer => {
          if (!customer.phone) return false;

          const cleanPhone = customer.phone.replace(/\D/g, ''); // إزالة كل شيء عدا الأرقام
          const cleanSearch = searchTerm.replace(/\D/g, '');

          return cleanPhone.includes(cleanSearch) ||
                 cleanPhone.startsWith(cleanSearch) ||
                 cleanPhone.endsWith(cleanSearch);
        });

        console.log(`📞 تم العثور على ${matchingCustomers.length} عميل مطابق لرقم الهاتف`);

        if (matchingCustomers.length > 0) {
          // البحث عن طلبات الصيانة لهؤلاء العملاء
          const customerIds = matchingCustomers.map(c => c.id);
          const phoneResults: MaintenanceRecord[] = [];

          for (const customerId of customerIds) {
            const customerQuery = query(
              maintenanceCollection,
              where('customerId', '==', customerId)
            );
            const customerSnapshot = await getDocs(customerQuery);

            customerSnapshot.docs.forEach(doc => {
              phoneResults.push(mapFirestoreDataToMaintenanceRecord(doc.id, doc.data()));
            });
          }

          console.log(`📞 البحث الشامل: ${phoneResults.length} نتيجة`);
          return phoneResults;
        }
      } catch (error) {
        console.error('خطأ في البحث الشامل برقم الهاتف:', error);
      }
    }

    console.log(`✅ إجمالي النتائج: ${finalResults.length}`);
    return finalResults;
  } catch (error) {
    console.error('Error searching maintenance records:', error);

    // في حالة الخطأ، محاولة البحث في البيانات المخزنة محليًا كملاذ أخير
    try {
      const cachedData = localStorage.getItem('maintenance_cache');
      if (cachedData) {
        const maintenanceRecords = JSON.parse(cachedData);
        const searchTermLower = searchTerm.toLowerCase();

        return maintenanceRecords.filter((record: MaintenanceRecord) =>
          record.customerName?.toLowerCase().includes(searchTermLower) ||
          record.orderNumber?.toString().includes(searchTerm) ||
          record.issue?.toLowerCase().includes(searchTermLower) ||
          record.deviceType?.toLowerCase().includes(searchTermLower)
        );
      }
    } catch (fallbackError) {
      console.error('Error using cached data for maintenance search fallback:', fallbackError);
    }

    // إرجاع مصفوفة فارغة بدلاً من رمي استثناء
    return [];
  }
};

// Get all maintenance records without limit for comprehensive search
export const getAllMaintenanceRecordsUnlimited = async (
  statusFilter?: MaintenanceStatus
): Promise<MaintenanceRecord[]> => {
  try {
    let q;

    if (statusFilter) {
      q = query(
        getMaintenanceCollection(),
        where('status', '==', statusFilter),
        orderBy('receivedAt', 'desc')
      );
    } else {
      q = query(
        getMaintenanceCollection(),
        orderBy('receivedAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return mapFirestoreDataToMaintenanceRecord(doc.id, data);
    });
  } catch (error) {
    console.error('Error getting all maintenance records unlimited:', error);
    return [];
  }
};

// تحديث مدفوعات الصيانة مع تسجيل في الخزينة والنشاطات المتعلقة بالديون والمرتجعات
export const updateMaintenancePayment = async (
  maintenanceId: string,
  paymentData: {
    amount: number;
    paymentType: 'cash' | 'debt';
    userId: string;
    userName: string;
    notes?: string;
  }
): Promise<void> => {
  try {
    console.log('🔄 بدء تحديث مدفوعات الصيانة:', {
      maintenanceId,
      ...paymentData
    });

    const firestore = ensureFirestore();

    // الحصول على بيانات طلب الصيانة
    const maintenanceRef = doc(firestore, 'maintenance', maintenanceId);
    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      throw new Error('طلب الصيانة غير موجود');
    }

    const maintenance = maintenanceDoc.data() as MaintenanceRecord;

    console.log('📋 بيانات طلب الصيانة الحالية:', {
      id: maintenanceId,
      totalPaid: maintenance.totalPaid,
      finalCost: maintenance.finalCost,
      customerName: maintenance.customerName
    });

    // حساب المبلغ الجديد المدفوع
    const newPaidAmount = (maintenance.totalPaid || 0) + paymentData.amount;
    const remainingAmount = (maintenance.finalCost || maintenance.initialCost || 0) - newPaidAmount;

    // تحديد حالة الدفع الجديدة
    let newPaymentStatus: PaymentStatus = 'unpaid';
    if (newPaidAmount >= (maintenance.finalCost || maintenance.initialCost || 0)) {
      newPaymentStatus = 'fullyPaid';
    } else if (newPaidAmount > 0) {
      newPaymentStatus = 'partiallyPaid';
    }

    console.log('💰 حساب المبالغ:', {
      currentPaid: maintenance.totalPaid || 0,
      paymentAmount: paymentData.amount,
      newPaidAmount,
      remainingAmount,
      newPaymentStatus
    });

    // تحديث بيانات طلب الصيانة
    const updateData = {
      totalPaid: newPaidAmount,
      paymentStatus: newPaymentStatus,
      paymentType: paymentData.paymentType,
      paymentDate: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await updateDoc(maintenanceRef, updateData);

    console.log('✅ تم تحديث بيانات طلب الصيانة');

    // إضافة المدفوعة إلى الخزينة إذا كانت نقدية
    if (paymentData.paymentType === 'cash') {
      console.log('💰 إضافة المدفوعة النقدية إلى الخزينة...');

      await addCashDrawerTransaction({
        type: 'maintenance_payment',
        amount: paymentData.amount, // مبلغ موجب لإضافة إلى الخزينة
        description: `دفعة صيانة - ${maintenance.deviceType} ${maintenance.devices?.[0]?.size || 'غير محدد'}`,
        maintenanceId: maintenanceId,
        customerId: maintenance.customerId,
        customerName: maintenance.customerName,
        employeeId: paymentData.userId,
        employeeName: paymentData.userName,
        branchId: maintenance.branchId,
        notes: paymentData.notes || `دفعة صيانة من ${maintenance.customerName}`
      });

      console.log('✅ تم إضافة المدفوعة النقدية إلى الخزينة');

      // تسجيل النشاط
      try {
        await logActivity(
          ActivityType.MAINTENANCE_UPDATE,
          `دفعة صيانة ${paymentData.paymentType === 'cash' ? 'نقدية' : 'دين'}`,
          `تم تسديد ${paymentData.amount} جنيه ${paymentData.paymentType === 'cash' ? 'نقداً' : 'ديناً'} لصيانة العميل ${maintenance.customerName} بواسطة ${paymentData.userName}`,
          paymentData.userId,
          paymentData.userName,
          'user',
          'maintenance',
          maintenanceId,
          {
            level: ActivityLevel.MEDIUM,
            targetName: `${maintenance.customerName} - ${maintenanceId.substring(0, 8)}`,
            metadata: {
              amount: paymentData.amount,
              paymentType: paymentData.paymentType,
              customerName: maintenance.customerName,
              employeeName: paymentData.userName
            }
          }
        );
      } catch (activityError) {
        console.warn('⚠️ فشل في تسجيل النشاط:', activityError);
      }
    } else if (paymentData.paymentType === 'debt') {
      console.log('💳 إنشاء دين للصيانة...');

      // استيراد خدمات الديون
      const { createDebtFromMaintenance } = await import('./debtService');

      const debtAmount = remainingAmount;

      if (debtAmount > 0) {
        const debtDescription = `دين من طلب صيانة رقم ${maintenance.orderNumber} - ${maintenance.deviceType}`;

        await createDebtFromMaintenance(
          maintenanceId,
          maintenance.orderNumber,
          maintenance.customerId,
          debtAmount,
          debtDescription,
          paymentData.userId,
          paymentData.userName,
          maintenance.branchId,
          maintenance.branchName,
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 يوم من الآن
          `دين من صيانة ${maintenance.deviceType} للعميل ${maintenance.customerName}`
        );

        console.log('✅ تم إنشاء الدين بنجاح');

        // تسجيل النشاط
        try {
          await logActivity(
            ActivityType.MAINTENANCE_UPDATE,
            `دفعة صيانة دين`,
            `تم تسديد ${paymentData.amount} جنيه ديناً لصيانة العميل ${maintenance.customerName} بواسطة ${paymentData.userName}`,
            paymentData.userId,
            paymentData.userName,
            'user',
            'maintenance',
            maintenanceId,
            {
              level: ActivityLevel.MEDIUM,
              targetName: `${maintenance.customerName} - ${maintenanceId.substring(0, 8)}`,
              metadata: {
                amount: paymentData.amount,
                paymentType: paymentData.paymentType,
                customerName: maintenance.customerName,
                employeeName: paymentData.userName
              }
            }
          );
        } catch (activityError) {
          console.warn('⚠️ فشل في تسجيل النشاط:', activityError);
        }
      } else {
        console.log('ℹ️ لا يوجد مبلغ متبقي لإنشاء دين');
      }
    }

  } catch (error) {
    console.error('❌ خطأ في تحديث مدفوعات الصيانة:', error);
    throw error;
  }
};

// دالة معالجة أجور الفنيين بناءً على حالة الصيانة
const handleTechnicianEarningsByStatus = async (
  maintenanceId: string,
  status: MaintenanceStatus,
  maintenanceData: any
): Promise<void> => {
  try {
    console.log(`🔄 معالجة أجر الفني لطلب الصيانة ${maintenanceId} - الحالة: ${status}`);

    // التحقق من وجود فني مرتبط بالطلب
    if (!maintenanceData.technicianId) {
      console.log(`ℹ️ لا يوجد فني مرتبط بطلب الصيانة ${maintenanceId}`);
      return;
    }

    const { getTechnicianEarnings, getTechnicianById, recordTechnicianEarning, deleteTechnicianEarning } = await import('./technicianService');

    // الحالات التي يتم فيها حساب أجر الفني
    const shouldCalculateEarning = status === 'completed' || status === 'delivered';

    // الحالات التي يتم فيها خصم أجر الفني (مرتجع عادي فقط)
    const shouldDeductEarning = status === 'cancelled';

    if (shouldCalculateEarning) {
      console.log(`💰 حساب أجر الفني للحالة: ${status}`);

      // التحقق من وجود أجر مسجل مسبقاً
      const existingEarnings = await getTechnicianEarnings(
        maintenanceData.technicianId,
        new Date(maintenanceData.createdAt || maintenanceData.receivedAt),
        new Date()
      );

      const existingEarning = existingEarnings.find(earning => earning.maintenanceId === maintenanceId);

      if (!existingEarning) {
        // لا يوجد أجر مسجل، نسجل أجر جديد
        await recordTechnicianEarning(
          maintenanceData.technicianId,
          maintenanceId,
          maintenanceData.deviceType || 'غير محدد',
          maintenanceData.devices?.[0]?.size || '',
          maintenanceData.finalCost || maintenanceData.initialCost || 0,
          maintenanceData.branchId
        );
        console.log(`✅ تم تسجيل أجر الفني ${maintenanceData.technicianName} لطلب الصيانة ${maintenanceId}`);
      } else {
        // يوجد أجر مسجل مسبقاً، نتحقق من نوع الراتب
        const technician = await getTechnicianById(maintenanceData.technicianId);

        if (technician && technician.salaryType === 'variable') {
          // للراتب المتغير، نحدث الأجر بالتكلفة النهائية إذا تغيرت
          const currentCost = maintenanceData.finalCost || maintenanceData.initialCost || 0;
          if (existingEarning.baseAmount !== currentCost) {
            console.log(`🔄 تحديث أجر الفني المتغير بالتكلفة النهائية: ${currentCost}`);
            await recordTechnicianEarning(
              maintenanceData.technicianId,
              maintenanceId,
              maintenanceData.deviceType || 'غير محدد',
              maintenanceData.devices?.[0]?.size || '',
              currentCost,
              maintenanceData.branchId,
              existingEarning.earningAmount // الحفاظ على المبلغ المخصص المحفوظ مسبقاً
            );
            console.log(`✅ تم تحديث أجر الفني المتغير ${maintenanceData.technicianName} لطلب الصيانة ${maintenanceId}`);
          } else {
            console.log(`ℹ️ أجر الفني المتغير محفوظ مسبقاً ولا يحتاج تحديث لطلب الصيانة ${maintenanceId}`);
          }
        } else {
          console.log(`ℹ️ أجر الفني مسجل مسبقاً لطلب الصيانة ${maintenanceId}`);
        }
      }
    } else if (shouldDeductEarning) {
      console.log(`❌ معالجة خصم أجر الفني للحالة: ${status}`);

      // البحث عن أجر الفني المسجل
      const existingEarnings = await getTechnicianEarnings(
        maintenanceData.technicianId,
        new Date(maintenanceData.createdAt || maintenanceData.receivedAt),
        new Date()
      );

      const existingEarning = existingEarnings.find(earning => earning.maintenanceId === maintenanceId);

      if (existingEarning) {
        // التحقق من نوع الإرجاع
        const isRejectedReturn = maintenanceData.returnReason === 'rejected' ||
                                maintenanceData.isRejectedReturn === true ||
                                maintenanceData.notes?.includes('رفض الجهاز');

        if (!isRejectedReturn) {
          // مرتجع عادي - خصم من الفني
          await deleteTechnicianEarning(existingEarning.id);
          console.log(`✅ تم خصم أجر الفني ${maintenanceData.technicianName} لطلب الصيانة المرتجع ${maintenanceId}`);
        } else {
          // مرتجع مع رفض الجهاز - لا يتم الخصم
          console.log(`ℹ️ مرتجع مع رفض الجهاز - لا يتم خصم أجر الفني لطلب الصيانة ${maintenanceId}`);
        }
      } else {
        console.log(`ℹ️ لا يوجد أجر مسجل للفني لطلب الصيانة ${maintenanceId}`);
      }
    } else {
      console.log(`ℹ️ الحالة ${status} لا تتطلب معالجة أجر الفني`);
    }

  } catch (error) {
    console.error('❌ فشل في معالجة أجر الفني:', error);
    // لا نوقف العملية إذا فشل تسجيل الأجر
  }
};

// دالة معالجة تغيير الفني
export const handleTechnicianChange = async (
  maintenanceId: string,
  oldTechnicianId: string,
  newTechnicianId: string,
  maintenanceData: any
): Promise<void> => {
  try {
    console.log(`🔄 معالجة تغيير الفني لطلب الصيانة ${maintenanceId}`);
    console.log(`👨‍🔧 من الفني: ${oldTechnicianId} إلى الفني: ${newTechnicianId}`);

    const { getTechnicianEarnings, getTechnicianById, recordTechnicianEarning, deleteTechnicianEarning } = await import('./technicianService');

    // الحصول على بيانات الفنيين
    const [oldTechnician, newTechnician] = await Promise.all([
      getTechnicianById(oldTechnicianId),
      getTechnicianById(newTechnicianId)
    ]);

    if (!oldTechnician || !newTechnician) {
      console.error('❌ لا يمكن العثور على بيانات الفنيين');
      return;
    }

    // البحث عن أجر الفني القديم
    const existingEarnings = await getTechnicianEarnings(
      oldTechnicianId,
      new Date(maintenanceData.createdAt || maintenanceData.receivedAt),
      new Date()
    );

    const existingEarning = existingEarnings.find(earning => earning.maintenanceId === maintenanceId);

    // خصم من الفني القديم
    if (existingEarning) {
      await deleteTechnicianEarning(existingEarning.id);
      console.log(`✅ تم خصم أجر الفني القديم ${oldTechnician.name}`);
    }

    // إضافة للفني الجديد حسب نوع راتبه
    if (newTechnician.salaryType === 'per_device' || newTechnician.salaryType === 'percentage' || newTechnician.salaryType === 'variable') {
      // للراتب بالقطعة أو النسبة أو المتغير - يُضاف للفني الجديد
      const customAmount = existingEarning?.earningAmount; // استخدام المبلغ المحفوظ إذا كان متاحاً

      await recordTechnicianEarning(
        newTechnicianId,
        maintenanceId,
        maintenanceData.deviceType || 'غير محدد',
        maintenanceData.devices?.[0]?.size || '',
        maintenanceData.finalCost || maintenanceData.initialCost || 0,
        maintenanceData.branchId,
        customAmount
      );
      console.log(`✅ تم إضافة أجر للفني الجديد ${newTechnician.name} (نوع الراتب: ${newTechnician.salaryType})`);
    } else if (newTechnician.salaryType === 'fixed') {
      // للراتب الثابت - يُخصم من الفني القديم فقط (لا يُضاف للجديد)
      console.log(`ℹ️ الفني الجديد ${newTechnician.name} براتب ثابت - لا يتم إضافة أجر إضافي`);
    }

    console.log(`✅ تم معالجة تغيير الفني بنجاح`);

  } catch (error) {
    console.error('❌ فشل في معالجة تغيير الفني:', error);
    throw error;
  }
};
